import React, { useState } from 'react';

// ========== CAPACITY NAVIGATOR v2.0 ==========
// Authority Governance Layer Implementation
// © 2026 Dr. Olutoyese Oyelese

// ========== INITIAL STATE ==========
const initialState = {
  patientInfo: {
    name: '',
    dob: '',
    sex: '',
    healthcareNumber: '',
    assessmentDate: new Date().toISOString().split('T')[0],
    assessorName: ''
  },
  assessmentType: {
    isDecisionSpecific: null,
    decisionDomain: '',
    decisionDomainOther: '',
    decisionDescription: ''
  },
  
  // NEW v2.0: Decision Context
  decisionContext: {
    decisionType: '',
    decisionTypeOther: '',
    irreversibilityTier: null,
    irreversibilityOverride: false,
    irreversibilityRationale: '',
    consequenceIfWrong: '',
    timeConstraint: '',
    priorAssessments: ''
  },
  
  // NEW v2.0: Legitimacy Gate
  legitimacyGate: {
    assessorRelationship: '',
    assessorConflict: [],
    coAssessorRequired: false,
    coAssessorName: '',
    patientObjection: null,
    patientObjectionDetails: '',
    coercionConcerns: [],
    legalProceedings: null,
    legalProceedingsDetails: '',
    emergencyContext: null,
    emergencyJustification: '',
    assessmentEnvironment: '',
    environmentFactors: [],
    gateOverride: false,
    gateOverrideRationale: ''
  },
  
  stations: {
    C1: { standardizedResponse: '', standardizedScore: null, realProbe: '', realResponse: '', realScore: null, validatedScore: null, discrepancy: false },
    C2: { standardizedResponse: '', standardizedScore: null, realProbe: '', realResponse: '', realScore: null, validatedScore: null, discrepancy: false },
    C3: { standardizedResponse: '', standardizedScore: null, choiceMade: '', realProbe: '', realResponse: '', realScore: null, validatedScore: null, discrepancy: false },
    C4: { delayMinutes: '', standardizedResponse: '', standardizedScore: null, realProbe: '', realResponse: '', realScore: null, validatedScore: null, discrepancy: false },
    C5: { administered: false, triggers: [], collateralSources: [], valuesIdentified: '', response: '', score: null, outcome: '' },
    C6: { standardizedResponse: '', standardizedScore: null, modality: [], realProbe: '', realResponse: '', realScore: null, realModality: [], validatedScore: null, discrepancy: false },
    C7: { observations: [], score: null, repeatRequired: false, repeatOverride: false, reversibleFactors: [], repeatScheduled: '', optimalWindow: '' }
  }
};

// ========== DEMO CASE ==========
// Pre-populated case for demonstration purposes
// Scenario: 78-year-old with mild dementia being assessed for residential placement
// Outcome: SUPPORTED CAPACITY - showing middle-ground classification

const DEMO_CASE = {
  patientInfo: {
    name: 'Margaret Wilson (DEMO)',
    dob: '1947-03-15',
    sex: 'F',
    healthcareNumber: 'DEMO-1234567',
    assessmentDate: new Date().toISOString().split('T')[0],
    assessorName: 'Dr. Demo Assessor'
  },
  assessmentType: {
    isDecisionSpecific: true,
    decisionDomain: 'residential',
    decisionDomainOther: '',
    decisionDescription: 'Whether Mrs. Wilson can decide to move from her home to Sunrise Assisted Living facility, as recommended by her care team due to recent falls and difficulty managing medications.'
  },
  decisionContext: {
    decisionType: 'residential',
    decisionTypeOther: '',
    irreversibilityTier: 'high',
    irreversibilityOverride: false,
    irreversibilityRationale: '',
    consequenceIfWrong: 'If capacity underestimated: loss of autonomy and premature institutionalization. If capacity overestimated: continued falls risk, medication errors, potential hospitalization.',
    timeConstraint: 'weeks',
    priorAssessments: 'MMSE 24/30 three months ago. No formal capacity assessment previously.'
  },
  legitimacyGate: {
    assessorRelationship: 'treating-physician',
    assessorConflict: ['none'],
    coAssessorRequired: false,
    coAssessorName: '',
    patientObjection: false,
    patientObjectionDetails: '',
    coercionConcerns: ['none-identified'],
    legalProceedings: false,
    legalProceedingsDetails: '',
    emergencyContext: false,
    emergencyJustification: '',
    assessmentEnvironment: 'optimal',
    environmentFactors: [],
    gateOverride: false,
    gateOverrideRationale: ''
  },
  stations: {
    C1: { 
      standardizedResponse: 'You eat breakfast in the morning, lunch in the afternoon, and supper in the evening. They\'re all meals, and eating makes you not hungry anymore.', 
      standardizedScore: 3, 
      realProbe: 'Can you tell me in your own words what we just discussed about Sunrise Assisted Living?',
      realResponse: 'It\'s a place where I would live instead of my house. They would help me with my pills and there are people around if I fall. My daughter thinks it\'s safer.',
      realScore: 2, 
      validatedScore: 2, 
      discrepancy: false 
    },
    C2: { 
      standardizedResponse: 'Get a plate from the cupboard.', 
      standardizedScore: 4, 
      realProbe: 'Why do you think your doctor and family are suggesting you move to Sunrise?',
      realResponse: 'I know I\'ve had some falls. And sometimes I forget if I took my pills. They worry about me being alone. I suppose they might be right that it\'s getting harder.',
      realScore: 3, 
      validatedScore: 3, 
      discrepancy: false 
    },
    C3: { 
      standardizedResponse: 'I\'d probably take it. Better to have it and not need it than get wet. Though it is extra to carry.', 
      standardizedScore: 3,
      choiceMade: 'take',
      realProbe: 'What are the pros and cons of moving to Sunrise versus staying at home?',
      realResponse: 'If I stay home, I keep my independence and my garden. But I might fall again and no one would know. At Sunrise, I\'d be safer and have company, but I\'d lose my home and my privacy. It\'s hard to give up 40 years of memories.',
      realScore: 3, 
      validatedScore: 3, 
      discrepancy: false 
    },
    C4: { 
      delayMinutes: '8',
      standardizedResponse: 'We talked about meals... breakfast, lunch, and dinner. They\'re all food and stop you being hungry.', 
      standardizedScore: 3, 
      realProbe: 'Earlier we discussed Sunrise Assisted Living. What do you remember about it?',
      realResponse: 'It\'s where I might move to. They help with medications and there are staff if something happens. My daughter wants me to go because of my falls.',
      realScore: 2, 
      validatedScore: 2, 
      discrepancy: false 
    },
    C5: { 
      administered: false, 
      triggers: [], 
      collateralSources: [], 
      valuesIdentified: '', 
      response: '', 
      score: null, 
      outcome: '' 
    },
    C6: { 
      standardizedResponse: 'Take it. The umbrella.', 
      standardizedScore: 4, 
      modality: ['Verbal'],
      realProbe: 'What is your decision about moving to Sunrise Assisted Living?',
      realResponse: 'I think... I\'m willing to try it. I don\'t want to, but I understand why it might be better. Can I bring some of my things from home?',
      realScore: 3, 
      realModality: ['Verbal'],
      validatedScore: 3, 
      discrepancy: false 
    },
    C7: { 
      observations: ['Consistent throughout'], 
      score: 4, 
      repeatRequired: false, 
      repeatOverride: false, 
      reversibleFactors: [], 
      repeatScheduled: '', 
      optimalWindow: '' 
    }
  }
};

// ========== DECISION TYPE CONFIGURATION ==========
const DECISION_TYPES = [
  { value: 'routine-care', label: 'Routine Care', desc: 'Daily care choices, minor treatments', tier: 'low' },
  { value: 'treatment-consent', label: 'Treatment Consent', desc: 'Medical procedures, medication changes', tier: 'moderate' },
  { value: 'financial-poa', label: 'Financial POA Activation', desc: 'Power of attorney, asset management', tier: 'high' },
  { value: 'residential', label: 'Residential Placement', desc: 'Living situation, care facility', tier: 'high' },
  { value: 'research', label: 'Research Participation', desc: 'Clinical trial enrollment', tier: 'moderate' },
  { value: 'maid', label: 'MAID Request', desc: 'Medical Assistance in Dying', tier: 'extreme' },
  { value: 'life-sustaining', label: 'Life-Sustaining Treatment', desc: 'Withdrawal or withholding', tier: 'extreme' },
  { value: 'other', label: 'Other', desc: 'Specify and assign tier manually', tier: null }
];

const TIER_CONFIG = {
  low: {
    label: 'LOW',
    color: '#065f46',
    bg: '#d1fae5',
    description: 'Routine decisions with minimal long-term impact',
    requirements: ['Standard assessment protocol', 'Single assessor sufficient', 'Standard documentation']
  },
  moderate: {
    label: 'MODERATE',
    color: '#92400e',
    bg: '#fef3c7',
    description: 'Decisions with reversible but significant consequences',
    requirements: ['Standard assessment protocol', 'Consider repeat if unstable', 'Document reasoning thoroughly']
  },
  high: {
    label: 'HIGH',
    color: '#9a3412',
    bg: '#fed7aa',
    description: 'Decisions with substantial and difficult-to-reverse consequences',
    requirements: ['Elevated confidence required', 'Repeat assessment if any instability', 'Consider second opinion', 'Comprehensive documentation']
  },
  extreme: {
    label: 'EXTREME',
    color: '#991b1b',
    bg: '#fecaca',
    description: 'Irreversible decisions with permanent consequences',
    requirements: ['Maximum confidence required', 'Independent co-assessor mandatory', 'Ethics review recommended', 'Legal consultation advised', 'Extensive documentation']
  }
};

// ========== DOMAIN-SPECIFIC QUESTION SETS ==========
// Each domain has standardized probes for C1-C4 and C6
// C5 (Values Consistency) and C7 (Stability) are domain-independent

const DOMAIN_QUESTION_SETS = {
  medical: {
    label: 'Medical Decision',
    description: 'Treatment consent, procedures, medication changes',
    C1: {
      vignette: 'I\'m going to explain the treatment we\'re discussing, and then ask you to tell me what you understood.',
      informationToProvide: 'Explain: (1) What the treatment/procedure is, (2) Why it\'s being recommended, (3) What it involves, (4) Expected benefits, (5) Possible risks or side effects, (6) What happens without treatment.',
      probe: 'Can you tell me in your own words what we just discussed about this treatment?',
      followUp: ['What is the treatment for?', 'What would it involve?', 'What could happen if you don\'t have it?']
    },
    C2: {
      vignette: 'Now I want to understand how you see this applying to your situation.',
      probe: 'Why do you think the doctor is recommending this treatment for you specifically?',
      followUp: ['Do you believe you have the condition we discussed?', 'How do you think this treatment might help you?', 'What do you think would happen to you without treatment?']
    },
    C3: {
      vignette: 'I\'d like to understand how you\'re thinking through this decision.',
      probe: 'What are the pros and cons of having this treatment versus not having it?',
      followUp: ['What concerns do you have about the treatment?', 'What concerns do you have about not having it?', 'How did you weigh these factors?']
    },
    C4: {
      vignette: 'Earlier we discussed the treatment options. Let me check what you remember.',
      probe: 'Can you tell me what we discussed about your treatment options?',
      followUp: ['What were the main risks we talked about?', 'What were the expected benefits?']
    },
    C6: {
      probe: 'What is your decision about this treatment?',
      followUp: ['Are you choosing to have the treatment or not?', 'Is that your final decision?']
    }
  },
  
  residential: {
    label: 'Residential Decision',
    description: 'Living situation, care facility placement',
    C1: {
      vignette: 'I\'m going to explain the living situation options we\'re discussing, and then ask you to tell me what you understood.',
      informationToProvide: 'Explain: (1) Current living situation concerns, (2) Options being considered, (3) What each option involves (level of care, location, cost), (4) Support available at each, (5) What staying in current situation means.',
      probe: 'Can you tell me in your own words what we just discussed about where you might live?',
      followUp: ['What are the options we talked about?', 'What kind of help would you get at each place?', 'What are the concerns about staying where you are?']
    },
    C2: {
      vignette: 'Now I want to understand how you see this applying to your situation.',
      probe: 'Why do you think we\'re discussing where you should live?',
      followUp: ['What difficulties have you been having at home?', 'How do you think a different living situation might help you?', 'What do you think would happen if nothing changes?']
    },
    C3: {
      vignette: 'I\'d like to understand how you\'re thinking through this decision.',
      probe: 'What are the advantages and disadvantages of each living option we discussed?',
      followUp: ['What would you gain by moving?', 'What would you lose?', 'How did you weigh these factors?']
    },
    C4: {
      vignette: 'Earlier we discussed the living situation options. Let me check what you remember.',
      probe: 'Can you tell me what we discussed about your living options?',
      followUp: ['What were the main options?', 'What were the concerns about your current situation?']
    },
    C6: {
      probe: 'Where would you like to live?',
      followUp: ['Which option are you choosing?', 'Is that your final decision?']
    }
  },
  
  financial: {
    label: 'Financial Decision',
    description: 'Money management, assets, power of attorney',
    C1: {
      vignette: 'I\'m going to explain the financial matter we\'re discussing, and then ask you to tell me what you understood.',
      informationToProvide: 'Explain: (1) What the financial decision involves, (2) Current financial situation relevant to decision, (3) Options available, (4) Consequences of each option, (5) Who would be involved/responsible.',
      probe: 'Can you tell me in your own words what we just discussed about this financial matter?',
      followUp: ['What is the decision about?', 'What are the options?', 'What would happen with each option?']
    },
    C2: {
      vignette: 'Now I want to understand how you see this applying to your situation.',
      probe: 'How might this financial decision affect your situation?',
      followUp: ['Why is this decision being considered now?', 'How do you think this would help or change things for you?', 'What concerns do you have about your finances?']
    },
    C3: {
      vignette: 'I\'d like to understand how you\'re thinking through this decision.',
      probe: 'What are the benefits and risks of this financial decision?',
      followUp: ['What could go well?', 'What could go wrong?', 'How did you weigh these factors?']
    },
    C4: {
      vignette: 'Earlier we discussed the financial options. Let me check what you remember.',
      probe: 'Can you tell me what we discussed about your financial matters?',
      followUp: ['What were the main options?', 'What were the potential consequences?']
    },
    C6: {
      probe: 'What would you like to do regarding this financial matter?',
      followUp: ['Which option are you choosing?', 'Is that your final decision?']
    }
  },
  
  personal: {
    label: 'Personal Care Decision',
    description: 'Activities of daily living, accepting help, care services',
    C1: {
      vignette: 'I\'m going to explain the care options we\'re discussing, and then ask you to tell me what you understood.',
      informationToProvide: 'Explain: (1) What care needs have been identified, (2) What help is being offered, (3) What the help would involve, (4) What happens if help is accepted, (5) What happens if help is declined.',
      probe: 'Can you tell me in your own words what we just discussed about your care needs?',
      followUp: ['What kind of help is being offered?', 'What would the helpers do?', 'What are the concerns about managing without help?']
    },
    C2: {
      vignette: 'Now I want to understand how you see this applying to your situation.',
      probe: 'Why do you think we\'re discussing care support for you?',
      followUp: ['What difficulties have you been having?', 'How do you think this help might affect your daily life?', 'What do you think would happen without this support?']
    },
    C3: {
      vignette: 'I\'d like to understand how you\'re thinking through this decision.',
      probe: 'What are the good and bad things about accepting this help versus managing on your own?',
      followUp: ['What would you gain from having help?', 'What would you lose?', 'How did you weigh these factors?']
    },
    C4: {
      vignette: 'Earlier we discussed the care options. Let me check what you remember.',
      probe: 'Can you tell me what we discussed about your care needs?',
      followUp: ['What kind of help was offered?', 'What were the concerns we discussed?']
    },
    C6: {
      probe: 'What level of help would you like to accept?',
      followUp: ['Are you accepting or declining the care support?', 'Is that your final decision?']
    }
  }
};

// Helper function to get questions for a domain
const getQuestionSet = (domain) => {
  return DOMAIN_QUESTION_SETS[domain] || null;
};

// ========== SCORING RUBRICS ==========
const scoringRubrics = {
  C1: [
    { score: 0, text: 'Cannot paraphrase any element' },
    { score: 1, text: 'Fragments only' },
    { score: 2, text: 'Main idea, missing elements' },
    { score: 3, text: 'Most elements, minor gaps' },
    { score: 4, text: 'Full paraphrase' }
  ],
  C2: [
    { score: 0, text: 'Cannot answer / confused / no response' },
    { score: 1, text: 'Unrelated answer' },
    { score: 2, text: 'Partial / vague' },
    { score: 3, text: 'Correct but needed prompt or repetition' },
    { score: 4, text: 'Immediate correct' }
  ],
  C3: [
    { score: 0, text: 'Cannot engage / no response' },
    { score: 1, text: 'States choice, no reasoning' },
    { score: 2, text: 'Single factor' },
    { score: 3, text: 'Acknowledges tradeoff' },
    { score: 4, text: 'Full weighing with clear rationale' }
  ],
  C4: [
    { score: 0, text: 'No recall' },
    { score: 1, text: 'Recalls being told something, no content' },
    { score: 2, text: 'Recalls 1-2 elements, forgets core' },
    { score: 3, text: 'Recalls most elements, minor gaps' },
    { score: 4, text: 'Full recall, integrates' }
  ],
  C5: [
    { score: 0, text: 'Contradicts all known values, no explanation' },
    { score: 1, text: 'Contradicts most values, minimal coherence' },
    { score: 2, text: 'Partially aligns, unexplained contradictions' },
    { score: 3, text: 'Mostly consistent, minor tensions explained' },
    { score: 4, text: 'Fully consistent OR inconsistency explained' }
  ],
  C6: [
    { score: 0, text: 'Cannot express any preference by any means' },
    { score: 1, text: 'Ambiguous or contradictory' },
    { score: 2, text: 'Inconsistent with reasoning, no explanation' },
    { score: 3, text: 'Clear preference, minor articulation difficulty' },
    { score: 4, text: 'Clear, consistent, unambiguous' }
  ],
  C7: [
    { score: 0, text: 'Marked fluctuation, could not sustain' },
    { score: 1, text: 'Significant variability, rare coherent moments' },
    { score: 2, text: 'Moderate variability, responded to redirection' },
    { score: 3, text: 'Mild variability, mostly consistent' },
    { score: 4, text: 'Stable throughout' }
  ]
};

// ========== LEGITIMACY GATE LOGIC ==========
function evaluateLegitimacyGate(legitimacyGate, decisionContext) {
  const flags = [];
  let gateStatus = 'proceed';
  
  const tier = decisionContext.irreversibilityTier;
  
  // === BLOCKING CONDITIONS ===
  
  // Block: Active legal proceedings
  if (legitimacyGate.legalProceedings === true) {
    flags.push({
      severity: 'block',
      code: 'LG1_LEGAL_PROCEEDINGS',
      message: 'Capacity is subject to active legal proceedings',
      action: 'Obtain legal guidance before proceeding or defer to court process'
    });
    gateStatus = 'block';
  }
  
  // Block: Extreme irreversibility + coercion concerns
  if (tier === 'extreme' && 
      legitimacyGate.coercionConcerns.length > 0 && 
      !legitimacyGate.coercionConcerns.includes('none-identified')) {
    flags.push({
      severity: 'block',
      code: 'LG2_COERCION_HIGH_STAKES',
      message: 'Coercion concerns identified for extreme-irreversibility decision',
      action: 'Coercion must be resolved before capacity assessment can proceed'
    });
    gateStatus = 'block';
  }
  
  // === DEFER CONDITIONS ===
  
  // Defer: Poor environment + high/extreme decision
  if (legitimacyGate.assessmentEnvironment === 'poor' && ['high', 'extreme'].includes(tier)) {
    flags.push({
      severity: 'defer',
      code: 'LG3_INADEQUATE_ENVIRONMENT',
      message: 'Assessment environment inadequate for high-stakes decision',
      action: 'Reschedule assessment in appropriate setting'
    });
    if (gateStatus !== 'block') gateStatus = 'defer';
  }
  
  // === CO-ASSESSOR REQUIRED ===
  
  // Co-assessor: Extreme irreversibility always
  if (tier === 'extreme') {
    flags.push({
      severity: 'requirement',
      code: 'LG4_EXTREME_COASSESSOR',
      message: 'Extreme-irreversibility decision requires independent co-assessor',
      action: 'Second assessor must complete parallel assessment'
    });
    if (!['block', 'defer'].includes(gateStatus)) gateStatus = 'co-assessor-required';
  }
  
  // Co-assessor: Assessor conflict identified
  if (legitimacyGate.assessorConflict.length > 0 && !legitimacyGate.assessorConflict.includes('none')) {
    flags.push({
      severity: 'requirement',
      code: 'LG5_ASSESSOR_CONFLICT',
      message: 'Potential assessor conflict of interest identified',
      action: 'Independent co-assessor required to validate findings'
    });
    if (!['block', 'defer'].includes(gateStatus)) gateStatus = 'co-assessor-required';
  }
  
  // === PROCEED WITH FLAGS ===
  
  // Flag: Patient objection
  if (legitimacyGate.patientObjection === true) {
    flags.push({
      severity: 'flag',
      code: 'LG6_PATIENT_OBJECTION',
      message: 'Patient objects to capacity assessment',
      action: 'Document objection; may proceed if legally authorized'
    });
    if (gateStatus === 'proceed') gateStatus = 'proceed-with-flags';
  }
  
  // Flag: Coercion concerns (non-extreme)
  if (legitimacyGate.coercionConcerns.length > 0 && 
      !legitimacyGate.coercionConcerns.includes('none-identified') &&
      tier !== 'extreme') {
    flags.push({
      severity: 'flag',
      code: 'LG7_COERCION_CONCERN',
      message: 'Potential coercion or undue influence identified',
      action: 'Document concerns; consider in classification confidence'
    });
    if (gateStatus === 'proceed') gateStatus = 'proceed-with-flags';
  }
  
  // Flag: Suboptimal environment
  if (legitimacyGate.assessmentEnvironment === 'suboptimal') {
    flags.push({
      severity: 'flag',
      code: 'LG8_SUBOPTIMAL_ENVIRONMENT',
      message: 'Assessment environment suboptimal',
      action: 'Document limitations; consider repeat in better conditions'
    });
    if (gateStatus === 'proceed') gateStatus = 'proceed-with-flags';
  }
  
  // Flag: Emergency context
  if (legitimacyGate.emergencyContext === true) {
    flags.push({
      severity: 'flag',
      code: 'LG9_EMERGENCY',
      message: 'Emergency context — optimal assessment not possible',
      action: 'Classification is provisional; reassess when stable'
    });
    if (gateStatus === 'proceed') gateStatus = 'proceed-with-flags';
  }
  
  // Flag: Time pressure
  if (['hours', 'immediate'].includes(decisionContext.timeConstraint)) {
    flags.push({
      severity: 'flag',
      code: 'LG10_TIME_PRESSURE',
      message: 'Time-pressured assessment',
      action: 'Document constraints; classification may be provisional'
    });
    if (gateStatus === 'proceed') gateStatus = 'proceed-with-flags';
  }
  
  return { gateStatus, flags };
}

// ========== ESCALATION TIER LOGIC ==========
function calculateEscalationTier(capacityClass, decisionContext, legitimacyFlags) {
  const tier = decisionContext.irreversibilityTier;
  let escalation = 'standard'; // Default: assessment is complete, no escalation needed
  const reasons = [];
  
  // NOTE: Escalation is SUGGESTED, not required. Assessment validity is independent of escalation.
  // We only suggest escalation based on specific concerning conditions, NOT tier alone.
  
  // === LEGAL TIER (advisory) ===
  
  if (legitimacyFlags.some(f => f.code === 'LG1_LEGAL_PROCEEDINGS')) {
    escalation = 'legal';
    reasons.push('Active legal proceedings noted');
  }
  
  // === ETHICS TIER (advisory) ===
  
  // End-of-life decisions - ethics input may be valuable
  if (['maid', 'life-sustaining'].includes(decisionContext.decisionType) && escalation !== 'legal') {
    escalation = 'ethics';
    reasons.push('End-of-life decision domain');
  }
  
  // Coercion concerns + incapacity finding
  if (legitimacyFlags.some(f => f.code.includes('COERCION')) && 
      ['clear-incapacity', 'provisional-incapacity'].includes(capacityClass) &&
      escalation !== 'legal') {
    escalation = 'ethics';
    reasons.push('Coercion concerns with incapacity finding');
  }
  
  // Patient objects AND found to lack capacity
  if (legitimacyFlags.some(f => f.code === 'LG6_PATIENT_OBJECTION') && 
      capacityClass === 'clear-incapacity' &&
      !['legal', 'ethics'].includes(escalation)) {
    escalation = 'ethics';
    reasons.push('Patient objects to incapacity finding');
  }
  
  // === CONSULT TIER (advisory) ===
  
  // Contested capacity for extreme-irreversibility decisions
  if (capacityClass === 'contested-capacity' && tier === 'extreme' &&
      !['legal', 'ethics'].includes(escalation)) {
    escalation = 'consult';
    reasons.push('Contested capacity for extreme-irreversibility decision');
  }
  
  // Non-classifiable result
  if (capacityClass === 'non-classifiable' && !['legal', 'ethics'].includes(escalation)) {
    escalation = 'consult';
    reasons.push('Classification not possible — specialist input may help');
  }
  
  // Assessor conflict identified
  if (legitimacyFlags.some(f => f.code === 'LG5_ASSESSOR_CONFLICT') &&
      !['legal', 'ethics'].includes(escalation)) {
    escalation = 'consult';
    reasons.push('Assessor conflict identified — independent review may help');
  }
  
  const actions = {
    standard: [
      'Assessment complete — implement at assessor\'s discretion',
      'Document in medical record',
      'No additional consultation required'
    ],
    consult: [
      'Second opinion may strengthen assessment confidence',
      'Specialist referral available (neuropsychology, psychiatry, geriatrics)',
      'Implementation timing at assessor\'s discretion'
    ],
    ethics: [
      'Ethics consultation available given decision complexity',
      'Stakeholder input may be valuable',
      'Assessment remains valid; ethics input is advisory'
    ],
    legal: [
      'Legal guidance advisable given proceedings/complexity',
      'Classification remains clinically valid',
      'Implementation timing should consider legal context',
      'Document any legal consultation obtained'
    ]
  };
  
  return {
    tier: escalation,
    label: { 
      standard: 'STANDARD PATHWAY', 
      consult: 'CONSULTATION SUGGESTED', 
      ethics: 'ETHICS INPUT SUGGESTED', 
      legal: 'LEGAL GUIDANCE ADVISED' 
    }[escalation],
    reasons,
    actions: actions[escalation],
    isAdvisory: escalation !== 'standard' // Flag that this is advisory, not mandatory
  };
}

// ========== CAPACITY CLASS LOGIC (v2.0) ==========
function calculateCapacityClass(stations) {
  const scores = {
    C1: stations.C1.validatedScore,
    C2: stations.C2.validatedScore,
    C3: stations.C3.validatedScore,
    C4: stations.C4.validatedScore,
    C6: stations.C6.validatedScore,
    C7: stations.C7.score
  };
  if (stations.C5.administered) scores.C5 = stations.C5.score;
  
  const allScores = Object.values(scores).filter(s => s !== null);
  const coreScores = [scores.C1, scores.C2, scores.C3, scores.C4, scores.C6].filter(s => s !== null);
  const lowScoreCount = coreScores.filter(s => s <= 1).length;
  
  const ruleTrace = [];
  const c7Score = scores.C7;
  const c7Influence = {
    score: c7Score,
    label: c7Score === null ? 'Not assessed' : 
           c7Score >= 3 ? 'Stable (no modifier)' : 
           c7Score === 2 ? 'Moderate instability (classification uncertain)' :
           'Severe instability (classification blocked)',
    blocksClassification: c7Score !== null && c7Score <= 1,
    requiresRepeat: c7Score !== null && c7Score <= 2 && !stations.C7.repeatOverride,
    modifiesConfidence: c7Score !== null && c7Score === 2
  };
  
  if (allScores.length === 0) {
    return { class: null, title: null, action: null, ruleTrace: [{ rule: 'DATA_CHECK', fired: true, reason: 'No scores recorded' }], c7Influence };
  }
  
  // NC1: Severe instability
  const nc1 = c7Score !== null && c7Score <= 1 && !stations.C7.repeatOverride;
  ruleTrace.push({ rule: 'NC1_SEVERE_INSTABILITY', fired: nc1, condition: 'C7 ≤ 1 without clinician override', values: { C7: c7Score } });
  if (nc1) {
    return {
      class: 'non-classifiable', title: 'NON-CLASSIFIABLE',
      action: 'Cannot classify — severe instability. Treat reversible factors and reassess.',
      ruleTrace, c7Influence,
      escalation: { reason: 'Marked fluctuation prevents reliable assessment', required: ['Address reversible factors', 'Schedule reassessment at optimal cognitive window'], mustDocument: 'Instability pattern and planned reassessment' }
    };
  }
  
  // NC2: Contradictory profile
  const nc2 = scores.C1 >= 3 && scores.C6 !== null && scores.C6 === 0;
  ruleTrace.push({ rule: 'NC2_CONTRADICTORY_PROFILE', fired: nc2, condition: 'C1 ≥ 3 AND C6 = 0', values: { C1: scores.C1, C6: scores.C6 } });
  if (nc2) {
    return {
      class: 'non-classifiable', title: 'NON-CLASSIFIABLE',
      action: 'Cannot classify — expression barrier with intact comprehension. Explore alternative modalities.',
      ruleTrace, c7Influence,
      escalation: { reason: 'Patient may understand but cannot express', required: ['Trial alternative expression modalities', 'Speech-language pathology consult'], mustDocument: 'Modalities trialed and outcomes' }
    };
  }
  
  // NC3: Mixed extremes
  const hasZero = coreScores.some(s => s === 0);
  const hasFour = coreScores.some(s => s === 4);
  const nc3 = hasZero && hasFour && coreScores.length >= 3;
  ruleTrace.push({ rule: 'NC3_MIXED_EXTREMES', fired: nc3, condition: 'Core stations contain both 0 and 4 scores', values: { hasZero, hasFour } });
  if (nc3) {
    return {
      class: 'non-classifiable', title: 'NON-CLASSIFIABLE',
      action: 'Cannot classify — extreme variability suggests confounding factors.',
      ruleTrace, c7Influence,
      escalation: { reason: 'Profile inconsistency suggests confounders', required: ['Identify intact vs impaired stations', 'Assess for domain-specific interference'], mustDocument: 'Hypothesized cause of variability' }
    };
  }
  
  // R1: Clear capacity
  const rule1 = allScores.every(s => s >= 3) && c7Score >= 3;
  ruleTrace.push({ rule: 'R1_CLEAR_CAPACITY', fired: rule1, condition: 'All stations ≥ 3 AND C7 ≥ 3', values: { allAbove3: allScores.every(s => s >= 3), C7: c7Score } });
  if (rule1) return { class: 'clear-capacity', title: 'CLEAR CAPACITY', action: 'Patient decides', ruleTrace, c7Influence };
  
  // R2: Supported capacity
  const rule2 = allScores.some(s => s === 2) && !allScores.some(s => s <= 1) && c7Score >= 3;
  ruleTrace.push({ rule: 'R2_SUPPORTED_CAPACITY', fired: rule2, condition: 'Some stations = 2, none ≤ 1, C7 ≥ 3', values: { has2: allScores.some(s => s === 2), hasBelow2: allScores.some(s => s <= 1) } });
  if (rule2) return { class: 'supported-capacity', title: 'SUPPORTED CAPACITY', action: 'Patient decides with structured support', ruleTrace, c7Influence };
  
  // R3: Provisional incapacity
  const rule3 = lowScoreCount >= 2 && stations.C7.reversibleFactors.length > 0;
  ruleTrace.push({ rule: 'R3_PROVISIONAL_INCAPACITY', fired: rule3, condition: '≥2 core stations ≤ 1 AND reversible factors present', values: { lowScoreCount, reversibleFactors: stations.C7.reversibleFactors.length } });
  if (rule3) return { class: 'provisional-incapacity', title: 'PROVISIONAL INCAPACITY', action: 'Treat reversible factors first, reassess', ruleTrace, c7Influence };
  
  // R4a: Expression failure
  const rule4a = scores.C6 !== null && scores.C6 === 0;
  ruleTrace.push({ rule: 'R4a_EXPRESSION_FAILURE', fired: rule4a, condition: 'C6 = 0', values: { C6: scores.C6 } });
  if (rule4a) return { class: 'clear-incapacity', title: 'CLEAR INCAPACITY', action: 'Substitute decision-maker — patient cannot express choice', ruleTrace, c7Influence };
  
  // R4b: Majority impairment
  const rule4b = coreScores.length >= 3 && lowScoreCount >= 3;
  ruleTrace.push({ rule: 'R4b_MAJORITY_IMPAIRMENT', fired: rule4b, condition: '≥3 of 5 core stations ≤ 1', values: { lowScoreCount } });
  if (rule4b) return { class: 'clear-incapacity', title: 'CLEAR INCAPACITY', action: 'Substitute decision-maker, following known values', ruleTrace, c7Influence };
  
  // R5: Contested capacity (default)
  ruleTrace.push({ rule: 'R5_CONTESTED_CAPACITY', fired: true, condition: 'No other rule matched', values: { lowScoreCount, C7: c7Score } });
  return { class: 'contested-capacity', title: 'CONTESTED CAPACITY', action: 'Defer, reassess, identify support conditions', ruleTrace, c7Influence };
}

function getSupportRecommendations(stations) {
  const recommendations = [];
  if (stations.C4.validatedScore !== null && stations.C4.validatedScore <= 2) recommendations.push('C4 low: Provide written information; repeat at decision point');
  if (stations.C2.validatedScore !== null && stations.C2.validatedScore <= 2) recommendations.push('C2 low: Re-present with personal relevance; use concrete examples');
  if (stations.C3.validatedScore !== null && stations.C3.validatedScore <= 2) recommendations.push('C3 low: Simplify to two clear options; present consequences explicitly');
  if (stations.C7.score !== null && stations.C7.score <= 2) recommendations.push('C7 low: Schedule for optimal time; address reversible factors');
  if (stations.C6.validatedScore !== null && stations.C6.validatedScore <= 2) recommendations.push('C6 low: Offer alternative expression modalities');
  return recommendations;
}

// ========== MAIN APP ==========
function App() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [data, setData] = useState(initialState);
  const [isDemoMode, setIsDemoMode] = useState(false);
  
  // Load demo case function
  const loadDemoCase = () => {
    setData(DEMO_CASE);
    setIsDemoMode(true);
  };
  
  // Clear demo and start fresh
  const exitDemo = () => {
    setData(initialState);
    setCurrentScreen(0);
    setIsDemoMode(false);
  };
  
  // v2.0: Updated screen list with new screens
  const screens = [
    'Patient Info',      // 0
    'Assessment Type',   // 1
    'Decision Context',  // 2 - NEW
    'Legitimacy Gate',   // 3 - NEW
    'C1 Comprehension',  // 4
    'C2 Appreciation',   // 5
    'C3 Reasoning',      // 6
    'C4 Retention',      // 7
    'C5 Consistency',    // 8
    'C6 Expression',     // 9
    'C7 Stability',      // 10
    'Report'             // 11
  ];
  
  // Update functions
  const updatePatientInfo = (field, value) => {
    setData(prev => ({ ...prev, patientInfo: { ...prev.patientInfo, [field]: value } }));
  };
  
  const updateAssessmentType = (field, value) => {
    setData(prev => ({ ...prev, assessmentType: { ...prev.assessmentType, [field]: value } }));
  };
  
  const updateDecisionContext = (field, value) => {
    setData(prev => {
      const newContext = { ...prev.decisionContext, [field]: value };
      // Auto-set irreversibility tier based on decision type
      if (field === 'decisionType') {
        const typeConfig = DECISION_TYPES.find(t => t.value === value);
        if (typeConfig && typeConfig.tier) {
          newContext.irreversibilityTier = typeConfig.tier;
        } else {
          newContext.irreversibilityTier = null;
        }
      }
      return { ...prev, decisionContext: newContext };
    });
  };
  
  const updateLegitimacyGate = (field, value) => {
    setData(prev => ({ ...prev, legitimacyGate: { ...prev.legitimacyGate, [field]: value } }));
  };
  
  const updateStation = (station, field, value) => {
    setData(prev => ({
      ...prev,
      stations: { ...prev.stations, [station]: { ...prev.stations[station], [field]: value } }
    }));
  };
  
  const updateStationWithValidation = (station, field, value) => {
    setData(prev => {
      const newStations = { ...prev.stations, [station]: { ...prev.stations[station], [field]: value } };
      const s = newStations[station];
      const stdScore = field === 'standardizedScore' ? value : s.standardizedScore;
      const realScore = field === 'realScore' ? value : s.realScore;
      
      if (stdScore !== null) {
        if (!prev.assessmentType.isDecisionSpecific || realScore === null) {
          newStations[station].validatedScore = stdScore;
          newStations[station].discrepancy = false;
        } else {
          newStations[station].validatedScore = Math.max(stdScore, realScore);
          newStations[station].discrepancy = Math.abs(stdScore - realScore) >= 2;
        }
      }
      return { ...prev, stations: newStations };
    });
  };
  
  // Checkbox array handlers
  const handleArrayToggle = (section, field, value, checked) => {
    setData(prev => {
      const current = prev[section][field] || [];
      let newArray;
      if (value === 'none' || value === 'none-identified') {
        newArray = checked ? [value] : [];
      } else {
        newArray = checked 
          ? [...current.filter(v => v !== 'none' && v !== 'none-identified'), value]
          : current.filter(v => v !== value);
      }
      return { ...prev, [section]: { ...prev[section], [field]: newArray } };
    });
  };
  
  // Navigation
  const goNext = () => {
    if (currentScreen === 8 && !data.stations.C5.administered) {
      setCurrentScreen(9); // Skip C5
    } else {
      setCurrentScreen(prev => Math.min(prev + 1, screens.length - 1));
    }
  };
  
  const goPrev = () => {
    if (currentScreen === 9 && !data.stations.C5.administered) {
      setCurrentScreen(7); // Skip back over C5
    } else {
      setCurrentScreen(prev => Math.max(prev - 1, 0));
    }
  };
  
  const isDecisionSpecific = data.assessmentType.isDecisionSpecific;
  const currentTier = data.decisionContext.irreversibilityTier;
  const gateResult = evaluateLegitimacyGate(data.legitimacyGate, data.decisionContext);

  // ========== SCREEN RENDERS ==========
  
  const renderPatientInfo = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Patient Information</h2>
        <p className="card-subtitle">Enter patient demographics and assessment details</p>
      </div>
      
      {/* Demo Mode Button */}
      {!isDemoMode && (
        <div className="demo-launch-box">
          <div className="demo-launch-content">
            <span className="demo-icon">🎓</span>
            <div>
              <strong>New to Capacity Navigator?</strong>
              <p>Try the interactive demo with a pre-filled case to see how the assessment works.</p>
            </div>
          </div>
          <button className="btn btn-demo" onClick={loadDemoCase}>
            Load Demo Case →
          </button>
        </div>
      )}
      
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">Patient Name</label>
          <input type="text" className="form-input" value={data.patientInfo.name} onChange={(e) => updatePatientInfo('name', e.target.value)} placeholder="Full legal name" />
        </div>
        <div className="form-group">
          <label className="form-label required">Date of Birth</label>
          <input type="date" className="form-input" value={data.patientInfo.dob} onChange={(e) => updatePatientInfo('dob', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">Sex</label>
          <select className="form-select" value={data.patientInfo.sex} onChange={(e) => updatePatientInfo('sex', e.target.value)}>
            <option value="">Select...</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="X">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label required">Healthcare Number</label>
          <input type="text" className="form-input" value={data.patientInfo.healthcareNumber} onChange={(e) => updatePatientInfo('healthcareNumber', e.target.value)} placeholder="Provincial health number" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label required">Assessment Date</label>
          <input type="date" className="form-input" value={data.patientInfo.assessmentDate} onChange={(e) => updatePatientInfo('assessmentDate', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label required">Assessor Name</label>
          <input type="text" className="form-input" value={data.patientInfo.assessorName} onChange={(e) => updatePatientInfo('assessorName', e.target.value)} placeholder="Your name and credentials" />
        </div>
      </div>
      <div className="nav-buttons">
        <div></div>
        <button className="btn btn-primary btn-lg" onClick={goNext} disabled={!data.patientInfo.name || !data.patientInfo.dob || !data.patientInfo.sex || !data.patientInfo.healthcareNumber || !data.patientInfo.assessorName}>Continue →</button>
      </div>
    </div>
  );
  
  const renderAssessmentType = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Assessment Type</h2>
        <p className="card-subtitle">Define the scope of this capacity assessment</p>
      </div>
      <div className="form-group">
        <label className="form-label required">Is there a specific decision being assessed?</label>
        <div className="radio-group">
          <label className={`radio-item ${data.assessmentType.isDecisionSpecific === true ? 'selected' : ''}`}>
            <input type="radio" name="assessmentType" checked={data.assessmentType.isDecisionSpecific === true} onChange={() => updateAssessmentType('isDecisionSpecific', true)} />
            <div><strong>YES — Decision-Specific Assessment</strong><p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>Assessing capacity for a particular decision</p></div>
          </label>
          <label className={`radio-item ${data.assessmentType.isDecisionSpecific === false ? 'selected' : ''}`}>
            <input type="radio" name="assessmentType" checked={data.assessmentType.isDecisionSpecific === false} onChange={() => updateAssessmentType('isDecisionSpecific', false)} />
            <div><strong>NO — General Capacity Assessment</strong><p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>Baseline capacity screening using standardized vignettes only</p></div>
          </label>
        </div>
      </div>
      {data.assessmentType.isDecisionSpecific && (
        <div className="conditional-section">
          <div className="conditional-header"><span>📋</span> Decision-Specific Details</div>
          <div className="form-group">
            <label className="form-label required">Decision Domain</label>
            <div className="radio-group">
              {[{ value: 'medical', label: 'Medical (treatment, procedure)' }, { value: 'residential', label: 'Residential (living situation)' }, { value: 'financial', label: 'Financial (money, assets)' }, { value: 'personal', label: 'Personal Care (ADLs, support)' }, { value: 'other', label: 'Other' }].map(opt => (
                <label key={opt.value} className={`radio-item ${data.assessmentType.decisionDomain === opt.value ? 'selected' : ''}`}>
                  <input type="radio" name="decisionDomain" checked={data.assessmentType.decisionDomain === opt.value} onChange={() => updateAssessmentType('decisionDomain', opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          {data.assessmentType.decisionDomain === 'other' && (
            <div className="form-group">
              <label className="form-label">Specify Domain</label>
              <input type="text" className="form-input" value={data.assessmentType.decisionDomainOther} onChange={(e) => updateAssessmentType('decisionDomainOther', e.target.value)} placeholder="Describe the decision domain" />
            </div>
          )}
          <div className="form-group">
            <label className="form-label required">Decision Description</label>
            <textarea className="form-textarea" value={data.assessmentType.decisionDescription} onChange={(e) => updateAssessmentType('decisionDescription', e.target.value)} placeholder="Describe the specific decision being considered..." />
          </div>
        </div>
      )}
      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={goNext} disabled={data.assessmentType.isDecisionSpecific === null || (data.assessmentType.isDecisionSpecific && !data.assessmentType.decisionDescription)}>Continue →</button>
      </div>
    </div>
  );
  
  // NEW v2.0: Decision Context Screen
  const renderDecisionContext = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Decision Context</h2>
        <p className="card-subtitle">What decision will this capacity classification inform?</p>
      </div>
      
      <div className="form-group">
        <label className="form-label required">Decision Type & Irreversibility</label>
        <div className="radio-group">
          {DECISION_TYPES.map(opt => (
            <label key={opt.value} className={`radio-item ${data.decisionContext.decisionType === opt.value ? 'selected' : ''}`}>
              <input type="radio" name="decisionType" checked={data.decisionContext.decisionType === opt.value} onChange={() => updateDecisionContext('decisionType', opt.value)} />
              <div style={{ flex: 1 }}>
                <strong>{opt.label}</strong>
                <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>{opt.desc}</p>
              </div>
              {opt.tier && (
                <span className={`tier-badge tier-${opt.tier}`}>{opt.tier.toUpperCase()}</span>
              )}
            </label>
          ))}
        </div>
      </div>
      
      {data.decisionContext.decisionType === 'other' && (
        <>
          <div className="form-group">
            <label className="form-label required">Specify Decision Type</label>
            <input type="text" className="form-input" value={data.decisionContext.decisionTypeOther} onChange={(e) => updateDecisionContext('decisionTypeOther', e.target.value)} placeholder="Describe the decision type" />
          </div>
          <div className="form-group">
            <label className="form-label required">Irreversibility Tier</label>
            <div className="radio-group horizontal">
              {['low', 'moderate', 'high', 'extreme'].map(tier => (
                <label key={tier} className={`radio-item ${data.decisionContext.irreversibilityTier === tier ? 'selected' : ''}`}>
                  <input type="radio" name="manualTier" checked={data.decisionContext.irreversibilityTier === tier} onChange={() => updateDecisionContext('irreversibilityTier', tier)} />
                  <span className={`tier-badge tier-${tier}`}>{tier.toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
      
      {currentTier && (
        <div className={`tier-panel tier-${currentTier}`}>
          <h4>Irreversibility: {TIER_CONFIG[currentTier].label}</h4>
          <p>{TIER_CONFIG[currentTier].description}</p>
          <div style={{ marginTop: 'var(--space-md)' }}>
            <strong>Requirements:</strong>
            <ul style={{ marginTop: 'var(--space-sm)', paddingLeft: 'var(--space-lg)' }}>
              {TIER_CONFIG[currentTier].requirements.map((req, i) => <li key={i}>{req}</li>)}
            </ul>
          </div>
        </div>
      )}
      
      <div className="form-group">
        <label className="form-label">Time Constraint</label>
        <div className="radio-group horizontal">
          {[
            { value: 'none', label: 'No time pressure' },
            { value: 'days', label: 'Days' },
            { value: 'hours', label: 'Hours' },
            { value: 'immediate', label: 'Immediate' }
          ].map(opt => (
            <label key={opt.value} className={`radio-item ${data.decisionContext.timeConstraint === opt.value ? 'selected' : ''}`}>
              <input type="radio" name="timeConstraint" checked={data.decisionContext.timeConstraint === opt.value} onChange={() => updateDecisionContext('timeConstraint', opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Consequence if Classification is Wrong</label>
        <textarea className="form-textarea" value={data.decisionContext.consequenceIfWrong} onChange={(e) => updateDecisionContext('consequenceIfWrong', e.target.value)} placeholder="What could happen if capacity is incorrectly classified for this decision?" />
      </div>
      
      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={goNext} disabled={!data.decisionContext.decisionType || !currentTier}>Continue →</button>
      </div>
    </div>
  );
  
  // NEW v2.0: Legitimacy Gate Screen
  const renderLegitimacyGate = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Assessment Legitimacy</h2>
        <p className="card-subtitle">Are conditions appropriate for this assessment?</p>
      </div>
      
      {/* Assessor Section */}
      <div className="form-section">
        <h3 className="section-title">Assessor Factors</h3>
        
        <div className="form-group">
          <label className="form-label required">Your Relationship to Patient</label>
          <select className="form-select" value={data.legitimacyGate.assessorRelationship} onChange={(e) => updateLegitimacyGate('assessorRelationship', e.target.value)}>
            <option value="">Select...</option>
            <option value="treating-physician">Treating Physician</option>
            <option value="family-physician">Family Physician</option>
            <option value="consultant">Consultant (requested for assessment)</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Potential Conflicts of Interest</label>
          <div className="checkbox-group">
            {[
              { value: 'none', label: 'None identified' },
              { value: 'financial-interest', label: 'Financial interest in outcome' },
              { value: 'family-relationship', label: 'Personal/family relationship' },
              { value: 'institutional-pressure', label: 'Institutional pressure (bed, resources)' },
              { value: 'prior-conflict', label: 'Prior conflict with patient/family' }
            ].map(opt => (
              <label key={opt.value} className="checkbox-item">
                <input type="checkbox" checked={data.legitimacyGate.assessorConflict.includes(opt.value)} onChange={(e) => handleArrayToggle('legitimacyGate', 'assessorConflict', opt.value, e.target.checked)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      </div>
      
      {/* Context Section */}
      <div className="form-section">
        <h3 className="section-title">Context Factors</h3>
        
        <div className="form-group">
          <label className="form-label required">Does the patient object to this assessment?</label>
          <div className="radio-group horizontal">
            <label className={`radio-item ${data.legitimacyGate.patientObjection === false ? 'selected' : ''}`}>
              <input type="radio" name="patientObjection" checked={data.legitimacyGate.patientObjection === false} onChange={() => updateLegitimacyGate('patientObjection', false)} />
              No
            </label>
            <label className={`radio-item ${data.legitimacyGate.patientObjection === true ? 'selected' : ''}`}>
              <input type="radio" name="patientObjection" checked={data.legitimacyGate.patientObjection === true} onChange={() => updateLegitimacyGate('patientObjection', true)} />
              Yes
            </label>
          </div>
        </div>
        
        {data.legitimacyGate.patientObjection && (
          <div className="form-group">
            <label className="form-label">Patient Objection Details</label>
            <textarea className="form-textarea" value={data.legitimacyGate.patientObjectionDetails} onChange={(e) => updateLegitimacyGate('patientObjectionDetails', e.target.value)} placeholder="Document the patient's objection and basis for proceeding..." />
          </div>
        )}
        
        <div className="form-group">
          <label className="form-label">Coercion or Undue Influence Concerns</label>
          <div className="checkbox-group">
            {[
              { value: 'none-identified', label: 'None identified' },
              { value: 'family-pressure', label: 'Family pressure' },
              { value: 'institutional-pressure', label: 'Institutional pressure' },
              { value: 'financial-pressure', label: 'Financial pressure on patient' }
            ].map(opt => (
              <label key={opt.value} className="checkbox-item">
                <input type="checkbox" checked={data.legitimacyGate.coercionConcerns.includes(opt.value)} onChange={(e) => handleArrayToggle('legitimacyGate', 'coercionConcerns', opt.value, e.target.checked)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label className="form-label required">Is capacity subject to legal proceedings?</label>
          <div className="radio-group horizontal">
            <label className={`radio-item ${data.legitimacyGate.legalProceedings === false ? 'selected' : ''}`}>
              <input type="radio" name="legalProceedings" checked={data.legitimacyGate.legalProceedings === false} onChange={() => updateLegitimacyGate('legalProceedings', false)} />
              No
            </label>
            <label className={`radio-item ${data.legitimacyGate.legalProceedings === true ? 'selected' : ''}`}>
              <input type="radio" name="legalProceedings" checked={data.legitimacyGate.legalProceedings === true} onChange={() => updateLegitimacyGate('legalProceedings', true)} />
              Yes
            </label>
          </div>
        </div>
        
        {data.legitimacyGate.legalProceedings && (
          <div className="form-group">
            <label className="form-label">Legal Proceedings Details</label>
            <textarea className="form-textarea" value={data.legitimacyGate.legalProceedingsDetails} onChange={(e) => updateLegitimacyGate('legalProceedingsDetails', e.target.value)} placeholder="Describe the legal proceedings..." />
          </div>
        )}
        
        <div className="form-group">
          <label className="form-label required">Is this an emergency assessment?</label>
          <div className="radio-group horizontal">
            <label className={`radio-item ${data.legitimacyGate.emergencyContext === false ? 'selected' : ''}`}>
              <input type="radio" name="emergencyContext" checked={data.legitimacyGate.emergencyContext === false} onChange={() => updateLegitimacyGate('emergencyContext', false)} />
              No
            </label>
            <label className={`radio-item ${data.legitimacyGate.emergencyContext === true ? 'selected' : ''}`}>
              <input type="radio" name="emergencyContext" checked={data.legitimacyGate.emergencyContext === true} onChange={() => updateLegitimacyGate('emergencyContext', true)} />
              Yes — optimal assessment not possible
            </label>
          </div>
        </div>
      </div>
      
      {/* Environment Section */}
      <div className="form-section">
        <h3 className="section-title">Assessment Environment</h3>
        
        <div className="form-group">
          <label className="form-label required">Environment Quality</label>
          <div className="radio-group">
            {[
              { value: 'optimal', label: 'Optimal', desc: 'Private, quiet, adequate time, patient comfortable' },
              { value: 'adequate', label: 'Adequate', desc: 'Minor limitations, unlikely to affect assessment' },
              { value: 'suboptimal', label: 'Suboptimal', desc: 'Significant limitations, may affect reliability' },
              { value: 'poor', label: 'Poor', desc: 'Major limitations, assessment validity questionable' }
            ].map(opt => (
              <label key={opt.value} className={`radio-item ${data.legitimacyGate.assessmentEnvironment === opt.value ? 'selected' : ''}`}>
                <input type="radio" name="environment" checked={data.legitimacyGate.assessmentEnvironment === opt.value} onChange={() => updateLegitimacyGate('assessmentEnvironment', opt.value)} />
                <div>
                  <strong>{opt.label}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
        
        {['suboptimal', 'poor'].includes(data.legitimacyGate.assessmentEnvironment) && (
          <div className="form-group">
            <label className="form-label">Environment Factors</label>
            <div className="checkbox-group">
              {['Noise', 'Interruptions', 'Lack of privacy', 'Time pressure', 'Patient fatigue'].map(factor => (
                <label key={factor} className="checkbox-item">
                  <input type="checkbox" checked={data.legitimacyGate.environmentFactors.includes(factor)} onChange={(e) => handleArrayToggle('legitimacyGate', 'environmentFactors', factor, e.target.checked)} />
                  {factor}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Gate Outcome */}
      <div className={`gate-outcome gate-${gateResult.gateStatus}`}>
        <h3>Gate Status: {gateResult.gateStatus.toUpperCase().replace(/-/g, ' ')}</h3>
        
        {gateResult.flags.length > 0 && (
          <div className="gate-flags">
            {gateResult.flags.map((flag, i) => (
              <div key={i} className={`gate-flag flag-${flag.severity}`}>
                <strong>{flag.code}</strong>: {flag.message}
                <p className="flag-action">→ {flag.action}</p>
              </div>
            ))}
          </div>
        )}
        
        {gateResult.gateStatus === 'block' && (
          <div className="gate-block-warning">
            <p>⛔ Assessment cannot proceed until blocking conditions are resolved.</p>
            <label className="checkbox-item" style={{ marginTop: 'var(--space-md)' }}>
              <input type="checkbox" checked={data.legitimacyGate.gateOverride} onChange={(e) => updateLegitimacyGate('gateOverride', e.target.checked)} />
              Override block (requires documented justification)
            </label>
            {data.legitimacyGate.gateOverride && (
              <textarea className="form-textarea" style={{ marginTop: 'var(--space-sm)' }} value={data.legitimacyGate.gateOverrideRationale} onChange={(e) => updateLegitimacyGate('gateOverrideRationale', e.target.value)} placeholder="Document justification for proceeding despite blocking condition..." />
            )}
          </div>
        )}
        
        {gateResult.gateStatus === 'co-assessor-required' && (
          <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
            <label className="form-label">Co-Assessor Name</label>
            <input type="text" className="form-input" value={data.legitimacyGate.coAssessorName} onChange={(e) => updateLegitimacyGate('coAssessorName', e.target.value)} placeholder="Name of independent co-assessor" />
          </div>
        )}
      </div>
      
      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={goNext} disabled={
          !data.legitimacyGate.assessorRelationship || 
          data.legitimacyGate.patientObjection === null || 
          data.legitimacyGate.legalProceedings === null || 
          data.legitimacyGate.emergencyContext === null || 
          !data.legitimacyGate.assessmentEnvironment ||
          (gateResult.gateStatus === 'block' && !data.legitimacyGate.gateOverride) ||
          (gateResult.gateStatus === 'block' && data.legitimacyGate.gateOverride && !data.legitimacyGate.gateOverrideRationale)
        }>
          {gateResult.gateStatus === 'proceed' ? 'Begin Assessment →' : 'Proceed with Flags →'}
        </button>
      </div>
    </div>
  );
  
  // ===== STATION RENDERS =====
  const renderStation = (station, config) => {
    const { title, subtitle, vignette, probe, followUpProbes, expectedAnswer, noteText, showChoiceRadio, isRetention } = config;
    const stationData = data.stations[station];
    const rubric = scoringRubrics[station];
    
    // Get domain-specific questions if available
    const domain = data.assessmentType.decisionDomain;
    const domainQuestions = domain && DOMAIN_QUESTION_SETS[domain] ? DOMAIN_QUESTION_SETS[domain][station] : null;
    
    // Get the default probe text for the real decision
    const getDefaultRealProbe = () => {
      if (domainQuestions) return domainQuestions.probe;
      return config.realProbeText || probe;
    };
    
    return (
      <div className="card">
        <div className="station-badge">{station}</div>
        <div className="card-header">
          <h2 className="card-title">{title}</h2>
          <p className="card-subtitle">{subtitle}</p>
        </div>
        
        {noteText && (
          <div className={`note-box ${isRetention ? 'timer-note' : ''}`}>
            <span className="note-icon">{isRetention ? '⏱' : 'ℹ️'}</span>
            <span className="note-text">{noteText}</span>
          </div>
        )}
        
        {isRetention && (
          <div className="form-group">
            <label className="form-label">Delay since C1 (minutes)</label>
            <input type="number" className="form-input" style={{ maxWidth: '150px' }} value={stationData.delayMinutes} onChange={(e) => updateStation(station, 'delayMinutes', e.target.value)} placeholder="e.g., 5" />
          </div>
        )}
        
        {/* STANDARDIZED ASSESSMENT SECTION */}
        <div className="assessment-section standardized">
          <div className="section-header">
            <span className="section-badge std">STANDARDIZED</span>
            <span className="section-desc">Baseline validation using neutral vignette</span>
          </div>
          
          <div className="vignette-box">
            <div className="vignette-label">{isRetention ? 'Standardized Probe — Read Aloud' : 'Standardized Vignette — Read Aloud'}</div>
            <div className="vignette-text">{vignette}</div>
          </div>
          
          {!isRetention && (
            <div className="probe-box">
              <div className="probe-label">Probe</div>
              <div className="probe-text">"{probe}"</div>
            </div>
          )}
          
          {followUpProbes && (
            <div className="probe-box" style={{ background: '#f0f9ff', border: '1px solid #b6d9f7' }}>
              <div className="probe-label" style={{ color: 'var(--accent)' }}>Follow-up (if needed)</div>
              {followUpProbes.map((p, i) => <div key={i} className="probe-text">"{p}"</div>)}
            </div>
          )}
          
          {expectedAnswer && <div className="expected-answer"><strong>Expected Answer:</strong> {expectedAnswer}</div>}
          
          <div className="form-group">
            <label className="form-label">Patient Response</label>
            <textarea className="form-textarea" value={stationData.standardizedResponse} onChange={(e) => updateStation(station, 'standardizedResponse', e.target.value)} placeholder="Record patient's response verbatim..." />
          </div>
          
          {showChoiceRadio && (
            <div className="form-group">
              <label className="form-label">Choice Made</label>
              <div className="radio-group" style={{ flexDirection: 'row', gap: '1rem' }}>
                <label className={`radio-item ${stationData.choiceMade === 'take' ? 'selected' : ''}`}>
                  <input type="radio" name="choiceMade" checked={stationData.choiceMade === 'take'} onChange={() => updateStation(station, 'choiceMade', 'take')} />
                  Take umbrella
                </label>
                <label className={`radio-item ${stationData.choiceMade === 'leave' ? 'selected' : ''}`}>
                  <input type="radio" name="choiceMade" checked={stationData.choiceMade === 'leave'} onChange={() => updateStation(station, 'choiceMade', 'leave')} />
                  Leave it
                </label>
              </div>
            </div>
          )}
          
          <div className="score-group">
            <div className="score-label">Standardized Score (0-4)</div>
            <div className="score-options">
              {rubric.map(({ score, text }) => (
                <label key={score} className={`score-option ${stationData.standardizedScore === score ? 'selected' : ''}`}>
                  <input type="radio" name={`${station}-standardized-score`} checked={stationData.standardizedScore === score} onChange={() => updateStationWithValidation(station, 'standardizedScore', score)} />
                  <span className="score-number">{score}</span>
                  <span className="score-description">{text}</span>
                </label>
              ))}
            </div>
            {stationData.standardizedScore !== null && <div className="score-display">Standardized Score: {stationData.standardizedScore}/4</div>}
          </div>
        </div>
        
        {/* DECISION-SPECIFIC ASSESSMENT SECTION */}
        {isDecisionSpecific && (
          <div className="assessment-section decision-specific">
            <div className="section-header">
              <span className="section-badge real">DECISION-SPECIFIC</span>
              <span className="section-desc">
                {domainQuestions 
                  ? `${DOMAIN_QUESTION_SETS[domain].label} — Pre-populated questions (editable)` 
                  : 'Custom questions for the actual decision'}
              </span>
            </div>
            
            {/* Domain-specific guidance */}
            {domainQuestions && domainQuestions.informationToProvide && (
              <div className="info-to-provide">
                <div className="info-label">📋 Information to Provide to Patient</div>
                <div className="info-text">{domainQuestions.informationToProvide}</div>
              </div>
            )}
            
            {/* Domain-specific vignette/context if available */}
            {domainQuestions && domainQuestions.vignette && (
              <div className="vignette-box domain-vignette">
                <div className="vignette-label">Context — Adapt to Patient's Situation</div>
                <div className="vignette-text">{domainQuestions.vignette}</div>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Question Asked (Real Decision)</label>
              <textarea 
                className="form-textarea" 
                value={stationData.realProbe} 
                onChange={(e) => updateStation(station, 'realProbe', e.target.value)} 
                placeholder={getDefaultRealProbe()}
                style={{ minHeight: '80px' }} 
              />
              {domainQuestions && !stationData.realProbe && (
                <button 
                  className="btn btn-secondary btn-sm" 
                  style={{ marginTop: 'var(--space-sm)' }}
                  onClick={() => updateStation(station, 'realProbe', domainQuestions.probe)}
                >
                  Use suggested question
                </button>
              )}
            </div>
            
            {/* Domain-specific follow-ups */}
            {domainQuestions && domainQuestions.followUp && (
              <div className="probe-box" style={{ background: '#f0f9ff', border: '1px solid #b6d9f7' }}>
                <div className="probe-label" style={{ color: 'var(--accent)' }}>Suggested Follow-ups (if needed)</div>
                {domainQuestions.followUp.map((p, i) => <div key={i} className="probe-text">"{p}"</div>)}
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Patient Response (Real Decision)</label>
              <textarea className="form-textarea" value={stationData.realResponse} onChange={(e) => updateStation(station, 'realResponse', e.target.value)} placeholder="Record patient's response to the real decision probe..." />
            </div>
            
            <div className="score-group">
              <div className="score-label">Real Decision Score (0-4)</div>
              <div className="score-options">
                {rubric.map(({ score, text }) => (
                  <label key={score} className={`score-option ${stationData.realScore === score ? 'selected' : ''}`}>
                    <input type="radio" name={`${station}-real-score`} checked={stationData.realScore === score} onChange={() => updateStationWithValidation(station, 'realScore', score)} />
                    <span className="score-number">{score}</span>
                    <span className="score-description">{text}</span>
                  </label>
                ))}
              </div>
              {stationData.realScore !== null && <div className="score-display">Real Decision Score: {stationData.realScore}/4</div>}
            </div>
          </div>
        )}
        
        {/* VALIDATED SCORE */}
        {stationData.validatedScore !== null && (
          <div style={{ marginTop: 'var(--space-xl)', padding: 'var(--space-lg)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="score-display validated">VALIDATED SCORE: {stationData.validatedScore}/4</div>
            <div className="validation-flags">
              <div className="flag">
                <span className={`flag-indicator ${stationData.discrepancy ? 'discrepancy' : 'consistent'}`}></span>
                {stationData.discrepancy ? 'Discrepancy (gap ≥ 2)' : 'Consistent'}
              </div>
            </div>
            {stationData.discrepancy && <div className="discrepancy-note">{isRetention ? 'Consider written aids' : 'Review presentation method'}</div>}
          </div>
        )}
        
        <div className="nav-buttons">
          <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
          <button className="btn btn-primary btn-lg" onClick={goNext} disabled={stationData.standardizedScore === null}>Continue →</button>
        </div>
      </div>
    );
  };
  
  const renderC1 = () => renderStation('C1', {
    title: 'C1 — Comprehension',
    subtitle: 'Can they receive information?',
    vignette: 'In the morning you have breakfast, in the afternoon you have lunch, in the evening you have supper. The common thing between all these meals is that you are eating food. If you were hungry, it makes the hunger go away.',
    probe: 'Can you tell me in your own words what I just said?',
    realProbeText: 'Can you tell me in your own words what we just talked about?'
  });
  
  const renderC2 = () => renderStation('C2', {
    title: 'C2 — Appreciation',
    subtitle: 'Do they grasp it applies to them?',
    vignette: 'If you didn\'t take a plate, you\'d have nowhere to put your food. If you can\'t put your food anywhere, you can\'t eat. If you can\'t eat, you stay hungry. How would you solve this?',
    probe: 'How would you solve this?',
    expectedAnswer: 'Get / take / find a plate',
    realProbeText: 'In your situation, [PROBLEM]. If that happens, [CONSEQUENCE]. What could be done about it?'
  });
  
  const renderC3 = () => renderStation('C3', {
    title: 'C3 — Reasoning',
    subtitle: 'Can they weigh options?',
    vignette: 'You\'re going out and see an umbrella by the door. There\'s a 50% chance of rain. If it doesn\'t rain, carrying the umbrella is extra weight. If it does rain, you\'ll get wet without it.',
    probe: 'Would you take the umbrella or leave it? Why?',
    showChoiceRadio: true,
    followUpProbes: ['What are the good things about taking it?', 'What are the bad things about taking it?', 'How did you decide?'],
    realProbeText: 'Tell me about the options you\'re considering. What are the pros and cons of each?'
  });
  
  const renderC4 = () => renderStation('C4', {
    title: 'C4 — Retention',
    subtitle: 'Can they hold information long enough to decide?',
    vignette: 'Earlier I told you about meals — breakfast, lunch, supper. Can you tell me what I said?',
    noteText: 'This should be administered after a delay (ideally 5+ minutes after C1). Record the actual delay time.',
    isRetention: true,
    realProbeText: 'What have we been discussing about your situation?'
  });
  
  const renderC5 = () => {
    const stationData = data.stations.C5;
    const rubric = scoringRubrics.C5;
    
    return (
      <div className="card">
        <div className="station-badge optional">C5</div>
        <div className="card-header">
          <h2 className="card-title">C5 — Values Consistency</h2>
          <p className="card-subtitle">Does their choice align with their known values?</p>
        </div>
        
        <div className="note-box">
          <span className="note-icon">ℹ️</span>
          <span className="note-text">This station is optional. Administer when: prior values are documented, choice seems inconsistent, or collateral is available.</span>
        </div>
        
        <div className="form-group">
          <label className="form-label">Administer C5?</label>
          <div className="radio-group" style={{ flexDirection: 'row', gap: '1rem' }}>
            <label className={`radio-item ${stationData.administered === true ? 'selected' : ''}`}>
              <input type="radio" name="c5administered" checked={stationData.administered === true} onChange={() => updateStation('C5', 'administered', true)} />
              Yes
            </label>
            <label className={`radio-item ${stationData.administered === false ? 'selected' : ''}`}>
              <input type="radio" name="c5administered" checked={stationData.administered === false} onChange={() => updateStation('C5', 'administered', false)} />
              No — Skip
            </label>
          </div>
        </div>
        
        {stationData.administered && (
          <>
            <div className="form-group">
              <label className="form-label">Triggers for C5 (select all that apply)</label>
              <div className="checkbox-group">
                {['Prior values documented', 'Choice seems inconsistent', 'Family/collateral concern', 'High-stakes decision'].map(trigger => (
                  <label key={trigger} className="checkbox-item">
                    <input type="checkbox" checked={stationData.triggers.includes(trigger)} onChange={(e) => {
                      const newTriggers = e.target.checked ? [...stationData.triggers, trigger] : stationData.triggers.filter(t => t !== trigger);
                      updateStation('C5', 'triggers', newTriggers);
                    }} />
                    {trigger}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Collateral Sources Consulted</label>
              <div className="checkbox-group">
                {['Chart/prior documentation', 'Family member', 'Substitute decision-maker', 'Care team', 'Advance directive'].map(source => (
                  <label key={source} className="checkbox-item">
                    <input type="checkbox" checked={stationData.collateralSources.includes(source)} onChange={(e) => {
                      const newSources = e.target.checked ? [...stationData.collateralSources, source] : stationData.collateralSources.filter(s => s !== source);
                      updateStation('C5', 'collateralSources', newSources);
                    }} />
                    {source}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Known Values Identified</label>
              <textarea className="form-textarea" value={stationData.valuesIdentified} onChange={(e) => updateStation('C5', 'valuesIdentified', e.target.value)} placeholder="Document the patient's known values, beliefs, and prior wishes..." />
            </div>
            
            <div className="probe-box">
              <div className="probe-label">Probe</div>
              <div className="probe-text">"This choice seems different from what you've said before about [VALUE]. Can you help me understand?"</div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Patient Response</label>
              <textarea className="form-textarea" value={stationData.response} onChange={(e) => updateStation('C5', 'response', e.target.value)} placeholder="Record patient's explanation..." />
            </div>
            
            <div className="score-group">
              <div className="score-label">Score (0-4)</div>
              <div className="score-options">
                {rubric.map(({ score, text }) => (
                  <label key={score} className={`score-option ${stationData.score === score ? 'selected' : ''}`}>
                    <input type="radio" name="c5-score" checked={stationData.score === score} onChange={() => updateStation('C5', 'score', score)} />
                    <span className="score-number">{score}</span>
                    <span className="score-description">{text}</span>
                  </label>
                ))}
              </div>
              {stationData.score !== null && <div className="score-display">C5 Score: {stationData.score}/4</div>}
            </div>
          </>
        )}
        
        <div className="nav-buttons">
          <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
          <button className="btn btn-primary btn-lg" onClick={goNext} disabled={stationData.administered && stationData.score === null}>Continue →</button>
        </div>
      </div>
    );
  };
  
  const renderC6 = () => {
    const stationData = data.stations.C6;
    const rubric = scoringRubrics.C6;
    const modalityOptions = ['Verbal', 'Written', 'Gesture', 'Eye gaze', 'Communication device', 'Family interpreter'];
    
    // Get domain-specific questions if available
    const domain = data.assessmentType.decisionDomain;
    const domainQuestions = domain && DOMAIN_QUESTION_SETS[domain] ? DOMAIN_QUESTION_SETS[domain].C6 : null;
    
    return (
      <div className="card">
        <div className="station-badge">C6</div>
        <div className="card-header">
          <h2 className="card-title">C6 — Expression</h2>
          <p className="card-subtitle">Can they communicate a choice?</p>
        </div>
        
        {/* STANDARDIZED SECTION */}
        <div className="assessment-section standardized">
          <div className="section-header">
            <span className="section-badge std">STANDARDIZED</span>
            <span className="section-desc">Baseline validation using neutral vignette</span>
          </div>
          
          <div className="vignette-box">
            <div className="vignette-label">Standardized Probe — Read Aloud</div>
            <div className="vignette-text">Going back to the umbrella question — what's your final answer: take it or leave it?</div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Patient Response</label>
            <textarea className="form-textarea" value={stationData.standardizedResponse} onChange={(e) => updateStation('C6', 'standardizedResponse', e.target.value)} placeholder="Record patient's response..." />
          </div>
          
          <div className="form-group">
            <label className="form-label">Modality Used</label>
            <div className="checkbox-group">
              {modalityOptions.map(mod => (
                <label key={mod} className="checkbox-item">
                  <input type="checkbox" checked={stationData.modality.includes(mod)} onChange={(e) => {
                    const newMods = e.target.checked ? [...stationData.modality, mod] : stationData.modality.filter(m => m !== mod);
                    updateStation('C6', 'modality', newMods);
                  }} />
                  {mod}
                </label>
              ))}
            </div>
          </div>
          
          <div className="score-group">
            <div className="score-label">Standardized Score (0-4)</div>
            <div className="score-options">
              {rubric.map(({ score, text }) => (
                <label key={score} className={`score-option ${stationData.standardizedScore === score ? 'selected' : ''}`}>
                  <input type="radio" name="c6-standardized-score" checked={stationData.standardizedScore === score} onChange={() => updateStationWithValidation('C6', 'standardizedScore', score)} />
                  <span className="score-number">{score}</span>
                  <span className="score-description">{text}</span>
                </label>
              ))}
            </div>
            {stationData.standardizedScore !== null && <div className="score-display">Standardized Score: {stationData.standardizedScore}/4</div>}
          </div>
        </div>
        
        {/* DECISION-SPECIFIC SECTION */}
        {isDecisionSpecific && (
          <div className="assessment-section decision-specific">
            <div className="section-header">
              <span className="section-badge real">DECISION-SPECIFIC</span>
              <span className="section-desc">
                {domainQuestions 
                  ? `${DOMAIN_QUESTION_SETS[domain].label} — Pre-populated questions (editable)` 
                  : 'Custom questions for the actual decision'}
              </span>
            </div>
            
            <div className="form-group">
              <label className="form-label">Question Asked (Real Decision)</label>
              <textarea 
                className="form-textarea" 
                value={stationData.realProbe} 
                onChange={(e) => updateStation('C6', 'realProbe', e.target.value)} 
                placeholder={domainQuestions ? domainQuestions.probe : 'What is your decision about [ACTUAL DECISION]?'}
                style={{ minHeight: '80px' }} 
              />
              {domainQuestions && !stationData.realProbe && (
                <button 
                  className="btn btn-secondary btn-sm" 
                  style={{ marginTop: 'var(--space-sm)' }}
                  onClick={() => updateStation('C6', 'realProbe', domainQuestions.probe)}
                >
                  Use suggested question
                </button>
              )}
            </div>
            
            {/* Domain-specific follow-ups */}
            {domainQuestions && domainQuestions.followUp && (
              <div className="probe-box" style={{ background: '#f0f9ff', border: '1px solid #b6d9f7' }}>
                <div className="probe-label" style={{ color: 'var(--accent)' }}>Suggested Follow-ups (if needed)</div>
                {domainQuestions.followUp.map((p, i) => <div key={i} className="probe-text">"{p}"</div>)}
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Patient Response (Real Decision)</label>
              <textarea className="form-textarea" value={stationData.realResponse} onChange={(e) => updateStation('C6', 'realResponse', e.target.value)} placeholder="Record patient's expression of their real decision..." />
            </div>
            
            <div className="form-group">
              <label className="form-label">Modality Used (Real Decision)</label>
              <div className="checkbox-group">
                {modalityOptions.map(mod => (
                  <label key={mod} className="checkbox-item">
                    <input type="checkbox" checked={stationData.realModality.includes(mod)} onChange={(e) => {
                      const newMods = e.target.checked ? [...stationData.realModality, mod] : stationData.realModality.filter(m => m !== mod);
                      updateStation('C6', 'realModality', newMods);
                    }} />
                    {mod}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="score-group">
              <div className="score-label">Real Decision Score (0-4)</div>
              <div className="score-options">
                {rubric.map(({ score, text }) => (
                  <label key={score} className={`score-option ${stationData.realScore === score ? 'selected' : ''}`}>
                    <input type="radio" name="c6-real-score" checked={stationData.realScore === score} onChange={() => updateStationWithValidation('C6', 'realScore', score)} />
                    <span className="score-number">{score}</span>
                    <span className="score-description">{text}</span>
                  </label>
                ))}
              </div>
              {stationData.realScore !== null && <div className="score-display">Real Decision Score: {stationData.realScore}/4</div>}
            </div>
          </div>
        )}
        
        {/* VALIDATED SCORE */}
        {stationData.validatedScore !== null && (
          <div style={{ marginTop: 'var(--space-xl)', padding: 'var(--space-lg)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="score-display validated">VALIDATED SCORE: {stationData.validatedScore}/4</div>
            <div className="validation-flags">
              <div className="flag">
                <span className={`flag-indicator ${stationData.discrepancy ? 'discrepancy' : 'consistent'}`}></span>
                {stationData.discrepancy ? 'Discrepancy (gap ≥ 2)' : 'Consistent'}
              </div>
            </div>
            {stationData.discrepancy && <div className="discrepancy-note">Consider alternative expression modalities</div>}
          </div>
        )}
        
        <div className="nav-buttons">
          <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
          <button className="btn btn-primary btn-lg" onClick={goNext} disabled={stationData.standardizedScore === null}>Continue →</button>
        </div>
      </div>
    );
  };
  
  const renderC7 = () => {
    const stationData = data.stations.C7;
    const rubric = scoringRubrics.C7;
    const observationOptions = ['Fluctuating attention', 'Variable responses', 'Fatigue observed', 'Time-of-day variation', 'Medication timing', 'Pain interference', 'Delirium features', 'Consistent throughout'];
    const reversibleOptions = ['Delirium', 'Medication effect', 'Pain', 'Metabolic (infection, electrolytes)', 'Sleep deprivation', 'Anxiety/distress', 'Sensory deficit (glasses, hearing aids)', 'Environmental (noise, interruptions)'];
    
    return (
      <div className="card">
        <div className="station-badge">C7</div>
        <div className="card-header">
          <h2 className="card-title">C7 — Stability</h2>
          <p className="card-subtitle">Is their capacity stable over time?</p>
        </div>
        
        <div className="note-box">
          <span className="note-icon">⏱</span>
          <span className="note-text">Assess throughout the evaluation. Low stability may require repeat assessment.</span>
        </div>
        
        <div className="form-group">
          <label className="form-label">Observations During Assessment</label>
          <div className="checkbox-group">
            {observationOptions.map(obs => (
              <label key={obs} className="checkbox-item">
                <input type="checkbox" checked={stationData.observations.includes(obs)} onChange={(e) => {
                  const newObs = e.target.checked ? [...stationData.observations, obs] : stationData.observations.filter(o => o !== obs);
                  updateStation('C7', 'observations', newObs);
                }} />
                {obs}
              </label>
            ))}
          </div>
        </div>
        
        <div className="score-group">
          <div className="score-label">Score (0-4)</div>
          <div className="score-options">
            {rubric.map(({ score, text }) => (
              <label key={score} className={`score-option ${stationData.score === score ? 'selected' : ''}`}>
                <input type="radio" name="c7-score" checked={stationData.score === score} onChange={() => {
                  updateStation('C7', 'score', score);
                  if (score <= 2) {
                    updateStation('C7', 'repeatRequired', true);
                    updateStation('C7', 'repeatOverride', false);
                  } else {
                    updateStation('C7', 'repeatRequired', false);
                  }
                }} />
                <span className="score-number">{score}</span>
                <span className="score-description">{text}</span>
              </label>
            ))}
          </div>
          {stationData.score !== null && <div className="score-display">C7 Score: {stationData.score}/4</div>}
        </div>
        
        {stationData.score !== null && stationData.score <= 2 && !stationData.repeatOverride && (
          <div className="conditional-section" style={{ borderColor: 'var(--warning)', background: '#fffbeb' }}>
            <div className="conditional-header" style={{ color: 'var(--warning)' }}><span>⚠️</span> Repeat Assessment Required</div>
            <p>Low stability score triggers mandatory reassessment unless clinician override.</p>
            
            <div className="form-group">
              <label className="form-label">Reversible Factors to Address</label>
              <div className="checkbox-group">
                {reversibleOptions.map(factor => (
                  <label key={factor} className="checkbox-item">
                    <input type="checkbox" checked={stationData.reversibleFactors.includes(factor)} onChange={(e) => {
                      const newFactors = e.target.checked ? [...stationData.reversibleFactors, factor] : stationData.reversibleFactors.filter(f => f !== factor);
                      updateStation('C7', 'reversibleFactors', newFactors);
                    }} />
                    {factor}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Repeat Assessment Scheduled</label>
                <input type="datetime-local" className="form-input" value={stationData.repeatScheduled} onChange={(e) => updateStation('C7', 'repeatScheduled', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Optimal Window</label>
                <input type="text" className="form-input" value={stationData.optimalWindow} onChange={(e) => updateStation('C7', 'optimalWindow', e.target.value)} placeholder="e.g., Morning after meds" />
              </div>
            </div>
            
            <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-secondary" onClick={() => { updateStation('C7', 'repeatOverride', true); updateStation('C7', 'repeatRequired', false); }}>
                ✓ Override — Repeat Assessment Not Required
              </button>
            </div>
          </div>
        )}
        
        {stationData.score !== null && stationData.score <= 2 && stationData.repeatOverride && (
          <div className="note-box" style={{ background: '#e6ffed', border: '1px solid #b7e7c7' }}>
            <span className="note-icon">✓</span>
            <span className="note-text" style={{ color: 'var(--success)' }}>Clinician override: Repeat assessment not required despite low stability score.</span>
            <button className="btn btn-secondary" style={{ marginLeft: 'auto', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }} onClick={() => { updateStation('C7', 'repeatOverride', false); updateStation('C7', 'repeatRequired', true); }}>
              Undo
            </button>
          </div>
        )}
        
        <div className="nav-buttons">
          <button className="btn btn-secondary" onClick={goPrev}>← Back</button>
          <button className="btn btn-success btn-lg" onClick={goNext} disabled={stationData.score === null}>Generate Report →</button>
        </div>
      </div>
    );
  };
  
  // ========== REPORT ==========
  const renderReport = () => {
    const { patientInfo, assessmentType, decisionContext, legitimacyGate, stations } = data;
    const capacityResult = calculateCapacityClass(stations);
    const recommendations = getSupportRecommendations(stations);
    const legitimacyResult = evaluateLegitimacyGate(legitimacyGate, decisionContext);
    const escalationResult = calculateEscalationTier(capacityResult?.class, decisionContext, legitimacyResult.flags);
    
    const stationList = [
      { key: 'C1', name: 'C1 Comprehension', score: stations.C1.validatedScore, hasDual: true, discrepancy: stations.C1.discrepancy },
      { key: 'C2', name: 'C2 Appreciation', score: stations.C2.validatedScore, hasDual: true, discrepancy: stations.C2.discrepancy },
      { key: 'C3', name: 'C3 Reasoning', score: stations.C3.validatedScore, hasDual: true, discrepancy: stations.C3.discrepancy },
      { key: 'C4', name: 'C4 Retention', score: stations.C4.validatedScore, hasDual: true, discrepancy: stations.C4.discrepancy },
      { key: 'C5', name: 'C5 Consistency', score: stations.C5.administered ? stations.C5.score : null, hasDual: false, na: !stations.C5.administered },
      { key: 'C6', name: 'C6 Expression', score: stations.C6.validatedScore, hasDual: true, discrepancy: stations.C6.discrepancy },
      { key: 'C7', name: 'C7 Stability', score: stations.C7.score, hasDual: false, repeatRequired: stations.C7.repeatRequired }
    ];
    
    const tierConfig = TIER_CONFIG[decisionContext.irreversibilityTier];
    const decisionTypeLabel = DECISION_TYPES.find(t => t.value === decisionContext.decisionType)?.label || decisionContext.decisionTypeOther;
    
    return (
      <div className="report-container">
        {/* Demo Watermark */}
        {isDemoMode && (
          <div className="demo-watermark">
            <div className="demo-watermark-text">DEMONSTRATION ONLY</div>
            <p>This is a sample case for training purposes. Not a real patient assessment.</p>
          </div>
        )}
        
        <div className="report-header">
          <h1 className="report-title">CAPACITY NAVIGATOR REPORT</h1>
          <p className="report-subtitle">Structured Capacity Assessment with Authority Governance</p>
        </div>
        
        {/* Patient Info */}
        <div className="report-section">
          <h3 className="report-section-title">Patient Information</h3>
          <div className="report-grid">
            <div className="report-item"><span className="report-label">Patient:</span><span className="report-value">{patientInfo.name}</span></div>
            <div className="report-item"><span className="report-label">HC#:</span><span className="report-value">{patientInfo.healthcareNumber}</span></div>
            <div className="report-item"><span className="report-label">DOB:</span><span className="report-value">{patientInfo.dob}</span></div>
            <div className="report-item"><span className="report-label">Sex:</span><span className="report-value">{patientInfo.sex}</span></div>
            <div className="report-item"><span className="report-label">Assessment Date:</span><span className="report-value">{patientInfo.assessmentDate}</span></div>
            <div className="report-item"><span className="report-label">Assessor:</span><span className="report-value">{patientInfo.assessorName}</span></div>
          </div>
        </div>
        
        {/* v2.0: Decision Context & Scope */}
        <div className="report-section">
          <h3 className="report-section-title">Decision Context & Scope</h3>
          <div className="report-grid">
            <div className="report-item">
              <span className="report-label">Decision Type:</span>
              <span className="report-value">{decisionTypeLabel}</span>
            </div>
            <div className="report-item">
              <span className="report-label">Irreversibility:</span>
              <span className={`tier-badge tier-${decisionContext.irreversibilityTier}`}>{tierConfig?.label}</span>
            </div>
            <div className="report-item">
              <span className="report-label">Time Constraint:</span>
              <span className="report-value">{decisionContext.timeConstraint || 'Not specified'}</span>
            </div>
            <div className="report-item">
              <span className="report-label">Environment:</span>
              <span className="report-value">{legitimacyGate.assessmentEnvironment}</span>
            </div>
            {assessmentType.decisionDomain && (
              <div className="report-item">
                <span className="report-label">Domain:</span>
                <span className="report-value">{assessmentType.decisionDomain === 'other' ? assessmentType.decisionDomainOther : assessmentType.decisionDomain.charAt(0).toUpperCase() + assessmentType.decisionDomain.slice(1)}</span>
              </div>
            )}
            {assessmentType.decisionDomain && DOMAIN_QUESTION_SETS[assessmentType.decisionDomain] && (
              <div className="report-item">
                <span className="report-label">Question Set:</span>
                <span className="report-value">{DOMAIN_QUESTION_SETS[assessmentType.decisionDomain].label}</span>
              </div>
            )}
          </div>
          {assessmentType.isDecisionSpecific && (
            <div className="scope-box">
              <strong>Specific Decision:</strong>
              <p>{assessmentType.decisionDescription}</p>
            </div>
          )}
          {decisionContext.consequenceIfWrong && (
            <p style={{ marginTop: 'var(--space-md)', fontSize: '0.9rem' }}><strong>Consequence if Wrong:</strong> {decisionContext.consequenceIfWrong}</p>
          )}
        </div>
        
        {/* v2.0: Legitimacy Flags */}
        {legitimacyResult.flags.length > 0 && (
          <div className="report-section legitimacy-flags-section">
            <h3 className="report-section-title">⚠️ Assessment Flags</h3>
            {legitimacyResult.flags.map((flag, i) => (
              <div key={i} className={`report-flag flag-${flag.severity}`}>
                <strong>{flag.code}:</strong> {flag.message}
                <span className="flag-action">→ {flag.action}</span>
              </div>
            ))}
            {legitimacyGate.gateOverride && (
              <div className="override-note">
                <strong>Gate Override Applied:</strong> {legitimacyGate.gateOverrideRationale}
              </div>
            )}
          </div>
        )}
        
        {/* Station Profile */}
        <div className="report-section">
          <h3 className="report-section-title">Station Profile</h3>
          <div className="score-chart">
            {stationList.map(s => (
              <div key={s.key} className="score-bar-container">
                <span className="score-bar-label">{s.name}</span>
                <div className="score-bar-track">
                  {s.score !== null && <div className={`score-bar-fill score-${s.score}`}></div>}
                  <span className="score-bar-value">{s.na ? 'N/A' : s.score !== null ? `${s.score}/4` : '—'}</span>
                </div>
              </div>
            ))}
          </div>
          <table className="station-table">
            <thead><tr><th>Station</th><th>Score</th><th>Type</th><th>Flag</th></tr></thead>
            <tbody>
              {stationList.map(s => (
                <tr key={s.key}>
                  <td>{s.name}</td>
                  <td className="score-cell">{s.na ? 'N/A' : s.score !== null ? `${s.score}/4` : '—'}</td>
                  <td>{s.na ? '—' : s.hasDual && assessmentType.isDecisionSpecific ? 'Real' : 'Std'}{s.repeatRequired && ' (Repeat)'}</td>
                  <td>{s.discrepancy && <span className="flag-badge gap">Gap</span>}{s.na && <span className="flag-badge na">N/A</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Clinical Documentation */}
        <div className="report-section">
          <h3 className="report-section-title">Clinical Documentation</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
            {assessmentType.isDecisionSpecific 
              ? `Questions asked and patient responses for the real decision assessment.${assessmentType.decisionDomain && DOMAIN_QUESTION_SETS[assessmentType.decisionDomain] ? ` Question set: ${DOMAIN_QUESTION_SETS[assessmentType.decisionDomain].label}` : ''}` 
              : 'Standardized assessment responses.'}
          </p>
          
          {/* C1 */}
          <div className="clinical-doc-station">
            <h4>C1 — Comprehension</h4>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Question Asked:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C1.realProbe) ? stations.C1.realProbe : 'Can you tell me in your own words what I just said?'}</span>
            </div>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Patient Response:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C1.realResponse) || stations.C1.standardizedResponse || '—'}</span>
            </div>
          </div>
          
          {/* C2 */}
          <div className="clinical-doc-station">
            <h4>C2 — Appreciation</h4>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Question Asked:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C2.realProbe) ? stations.C2.realProbe : 'How would you solve this?'}</span>
            </div>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Patient Response:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C2.realResponse) || stations.C2.standardizedResponse || '—'}</span>
            </div>
          </div>
          
          {/* C3 */}
          <div className="clinical-doc-station">
            <h4>C3 — Reasoning</h4>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Question Asked:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C3.realProbe) ? stations.C3.realProbe : 'Would you take the umbrella or leave it? Why?'}</span>
            </div>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Patient Response:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C3.realResponse) || stations.C3.standardizedResponse || '—'}</span>
            </div>
            {stations.C3.choiceMade && (
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Choice Made:</span>
                <span className="clinical-doc-value">{stations.C3.choiceMade === 'take' ? 'Take umbrella' : 'Leave it'}</span>
              </div>
            )}
          </div>
          
          {/* C4 */}
          <div className="clinical-doc-station">
            <h4>C4 — Retention</h4>
            {stations.C4.delayMinutes && (
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Delay (minutes):</span>
                <span className="clinical-doc-value">{stations.C4.delayMinutes}</span>
              </div>
            )}
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Question Asked:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C4.realProbe) ? stations.C4.realProbe : 'Earlier I told you about meals. Can you tell me what I said?'}</span>
            </div>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Patient Response:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C4.realResponse) || stations.C4.standardizedResponse || '—'}</span>
            </div>
          </div>
          
          {/* C5 - only if administered */}
          {stations.C5.administered && (
            <div className="clinical-doc-station">
              <h4>C5 — Values Consistency</h4>
              {stations.C5.valuesIdentified && (
                <div className="clinical-doc-item">
                  <span className="clinical-doc-label">Known Values:</span>
                  <span className="clinical-doc-value">{stations.C5.valuesIdentified}</span>
                </div>
              )}
              {stations.C5.collateralSources.length > 0 && (
                <div className="clinical-doc-item">
                  <span className="clinical-doc-label">Collateral Sources:</span>
                  <span className="clinical-doc-value">{stations.C5.collateralSources.join(', ')}</span>
                </div>
              )}
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Patient Response:</span>
                <span className="clinical-doc-value">{stations.C5.response || '—'}</span>
              </div>
            </div>
          )}
          
          {/* C6 */}
          <div className="clinical-doc-station">
            <h4>C6 — Expression</h4>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Question Asked:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C6.realProbe) ? stations.C6.realProbe : 'What is your final answer: take the umbrella or leave it?'}</span>
            </div>
            <div className="clinical-doc-item">
              <span className="clinical-doc-label">Patient Response:</span>
              <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C6.realResponse) || stations.C6.standardizedResponse || '—'}</span>
            </div>
            {((assessmentType.isDecisionSpecific && stations.C6.realModality.length > 0) || stations.C6.modality.length > 0) && (
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Modality Used:</span>
                <span className="clinical-doc-value">{(assessmentType.isDecisionSpecific && stations.C6.realModality.length > 0) ? stations.C6.realModality.join(', ') : stations.C6.modality.join(', ')}</span>
              </div>
            )}
          </div>
          
          {/* C7 */}
          <div className="clinical-doc-station">
            <h4>C7 — Stability</h4>
            {stations.C7.observations.length > 0 && (
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Observations:</span>
                <span className="clinical-doc-value">{stations.C7.observations.join(', ')}</span>
              </div>
            )}
            {stations.C7.optimalWindow && (
              <div className="clinical-doc-item">
                <span className="clinical-doc-label">Optimal Window:</span>
                <span className="clinical-doc-value">{stations.C7.optimalWindow}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* C7 Stability Influence */}
        {capacityResult?.c7Influence && (
          <div className="report-section" style={{ background: capacityResult.c7Influence.blocksClassification ? '#fef2f2' : capacityResult.c7Influence.modifiesConfidence ? '#fffbeb' : '#f0fdf4', border: `1px solid ${capacityResult.c7Influence.blocksClassification ? '#fecaca' : capacityResult.c7Influence.modifiesConfidence ? '#fde68a' : '#bbf7d0'}`, borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)' }}>
            <h3 className="report-section-title" style={{ color: capacityResult.c7Influence.blocksClassification ? '#dc2626' : capacityResult.c7Influence.modifiesConfidence ? '#d97706' : '#16a34a' }}>C7 Stability Influence</h3>
            <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
              <div><strong>C7 Score:</strong> {capacityResult.c7Influence.score !== null ? `${capacityResult.c7Influence.score}/4` : 'Not assessed'}</div>
              <div><strong>Classification Effect:</strong> {capacityResult.c7Influence.label}</div>
              {capacityResult.c7Influence.blocksClassification && (
                <div style={{ color: '#dc2626', fontWeight: 600 }}>⚠️ Severe instability BLOCKS capacity classification</div>
              )}
              {capacityResult.c7Influence.modifiesConfidence && (
                <div style={{ color: '#d97706' }}>⚡ Moderate instability — classification confidence reduced</div>
              )}
              {stations.C7.reversibleFactors.length > 0 && (
                <div><strong>Reversible Factors:</strong> {stations.C7.reversibleFactors.join(', ')}</div>
              )}
            </div>
          </div>
        )}
        
        {/* Capacity Classification */}
        {capacityResult && capacityResult.class && (
          <div className={`capacity-class ${capacityResult.class}`}>
            <h3 className="capacity-class-title">{capacityResult.title}</h3>
            <p className="capacity-class-action">"{capacityResult.action}"</p>
          </div>
        )}
        
        {/* v2.0: Escalation Tier */}
        <div className={`escalation-panel escalation-${escalationResult.tier}`}>
          <h3>Pathway: {escalationResult.label}</h3>
          {escalationResult.reasons.length > 0 && (
            <div className="escalation-reasons">
              <strong>Based on:</strong>
              <ul>{escalationResult.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          <div className="escalation-actions">
            <strong>Considerations (at assessor's discretion):</strong>
            <ul>{escalationResult.actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
          </div>
          {escalationResult.isAdvisory && (
            <p className="escalation-advisory">
              ℹ️ This assessment is complete and clinically valid. Above suggestions are advisory — implementation remains at the assessor's professional discretion.
            </p>
          )}
        </div>
        
        {/* Non-classifiable escalation */}
        {capacityResult?.escalation && (
          <div className="report-section" style={{ background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)' }}>
            <h3 className="report-section-title" style={{ color: '#dc2626' }}>⚠️ Classification Blocked</h3>
            <p><strong>Reason:</strong> {capacityResult.escalation.reason}</p>
            <div style={{ marginTop: 'var(--space-md)' }}>
              <strong>Next Steps:</strong>
              <ul style={{ paddingLeft: 'var(--space-xl)' }}>
                {capacityResult.escalation.required.map((req, i) => <li key={i}>{req}</li>)}
              </ul>
            </div>
            <p style={{ marginTop: 'var(--space-md)' }}><strong>Document:</strong> {capacityResult.escalation.mustDocument}</p>
          </div>
        )}
        
        {/* Decision Engine Trace */}
        {capacityResult?.ruleTrace && capacityResult.ruleTrace.length > 0 && (
          <div className="report-section">
            <h3 className="report-section-title">Decision Engine Trace</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>Rules evaluated in sequence. First rule to fire determines classification.</p>
            <table className="station-table" style={{ fontSize: '0.875rem' }}>
              <thead><tr><th>Rule</th><th>Condition</th><th>Result</th></tr></thead>
              <tbody>
                {capacityResult.ruleTrace.map((rule, i) => (
                  <tr key={i} style={{ background: rule.fired ? '#f0fdf4' : 'transparent' }}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.rule}</td>
                    <td>{rule.condition || rule.reason || '—'}</td>
                    <td style={{ fontWeight: rule.fired ? 600 : 400, color: rule.fired ? '#16a34a' : '#9ca3af' }}>
                      {rule.fired ? '✓ FIRED' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Support Recommendations */}
        {capacityResult?.class === 'supported-capacity' && recommendations.length > 0 && (
          <div className="report-section">
            <h3 className="report-section-title">Support Recommendations</h3>
            <ul className="recommendations-list">{recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}
        
        {/* Discrepancy Flags */}
        {stationList.some(s => s.discrepancy) && (
          <div className="report-section">
            <h3 className="report-section-title">Discrepancy Flags</h3>
            <p>Stations with gap ≥ 2 between standardized and real decision scores:</p>
            <ul style={{ marginTop: 'var(--space-sm)', paddingLeft: 'var(--space-xl)' }}>
              {stationList.filter(s => s.discrepancy).map(s => <li key={s.key}>{s.name}</li>)}
            </ul>
          </div>
        )}
        
        {/* v2.0: Classification Scope Limitation */}
        <div className="report-section scope-limitation">
          <h3 className="report-section-title">Classification Scope</h3>
          <p><strong>This classification applies ONLY to:</strong></p>
          <p className="scope-decision">{assessmentType.decisionDescription || 'General capacity screening'}</p>
          <p style={{ marginTop: 'var(--space-md)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            This classification does not determine capacity for any other decision. Capacity is decision-specific and time-specific.
          </p>
        </div>
        
        {/* What This Assessment Does Not Determine */}
        <div className="report-section">
          <h3 className="report-section-title">What This Assessment Does Not Determine</h3>
          <ul style={{ paddingLeft: 'var(--space-xl)', color: 'var(--text-secondary)' }}>
            <li>What the patient should choose</li>
            <li>Whether the clinician agrees with the choice</li>
            <li>Permanent capacity status (unless confirmed stable)</li>
            <li>Capacity for other decision domains</li>
          </ul>
        </div>
        
        {/* Follow-Up Required */}
        <div className="report-section">
          <h3 className="report-section-title">Follow-Up Required</h3>
          {stations.C7.repeatRequired ? (
            <>
              <p>☑ Repeat assessment scheduled</p>
              {stations.C7.repeatScheduled && <p>Date/Time: {stations.C7.repeatScheduled}</p>}
              {stations.C7.reversibleFactors.length > 0 && <p>Address reversible factors: {stations.C7.reversibleFactors.join(', ')}</p>}
            </>
          ) : stations.C7.repeatOverride ? (
            <p>☐ Clinician override — repeat assessment not required despite stability score ≤2</p>
          ) : capacityResult?.class === 'clear-capacity' ? <p>☑ None — capacity clear</p> : <p>☐ Per clinical judgment</p>}
        </div>
        
        {/* Footer */}
        <div className="report-footer">
          <div>
            <div className="signature-line">Assessor Signature</div>
            <div className="signature-line" style={{ marginTop: 'var(--space-md)' }}>Date</div>
            {legitimacyGate.coAssessorName && (
              <div className="signature-line" style={{ marginTop: 'var(--space-md)' }}>Co-Assessor: {legitimacyGate.coAssessorName}</div>
            )}
          </div>
          <div className="report-branding">
            <p><strong>CAPACITY NAVIGATOR</strong> v2.2</p>
            <p>© 2026 Dr. Olutoyese Oyelese</p>
          </div>
        </div>
        
        <div className="nav-buttons no-print">
          <button className="btn btn-secondary" onClick={goPrev}>← Back to Edit</button>
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <button className="btn btn-secondary" onClick={() => { 
              if (isDemoMode || window.confirm('Start a new assessment? Current data will be lost.')) { 
                setData(initialState); 
                setCurrentScreen(0); 
                setIsDemoMode(false);
              } 
            }}>
              {isDemoMode ? 'Exit Demo & Start New' : 'New Assessment'}
            </button>
            <button className="btn btn-primary btn-lg" onClick={() => window.print()}>🖨 Print Report</button>
          </div>
        </div>
      </div>
    );
  };
  
  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 0: return renderPatientInfo();
      case 1: return renderAssessmentType();
      case 2: return renderDecisionContext();
      case 3: return renderLegitimacyGate();
      case 4: return renderC1();
      case 5: return renderC2();
      case 6: return renderC3();
      case 7: return renderC4();
      case 8: return renderC5();
      case 9: return renderC6();
      case 10: return renderC7();
      case 11: return renderReport();
      default: return renderPatientInfo();
    }
  };
  
  return (
    <div className="app-container">
      <div style={{ background: "#080c14", padding: "8px 0", borderBottom: "1px solid rgba(100,116,139,0.15)" }} className="no-print"><div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}><a href="https://navigationalmind.com" style={{ fontFamily: "monospace", fontSize: 13, color: "#e2e8f0", textDecoration: "none", letterSpacing: "0.5px", display: "inline-flex", alignItems: "center", gap: 6 }}>← navigationalmind.com</a></div></div>
      <header className="header no-print">
        <div className="header-content">
          <div>
            <h1>Capacity Navigator</h1>
            <p className="header-subtitle">Structured Capacity Assessment Tool v2.2</p>
          </div>
        </div>
      </header>
      
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="demo-banner no-print">
          <div className="demo-banner-content">
            <span className="demo-badge">🎓 DEMO MODE</span>
            <span className="demo-text">
              <strong>Case: Mrs. Margaret Wilson</strong> — 78yo female, residential placement decision. 
              Click through screens to see how the assessment works.
            </span>
          </div>
          <button className="btn btn-demo-exit" onClick={exitDemo}>
            Exit Demo
          </button>
        </div>
      )}
      
      {currentScreen < 11 && (
        <div className="progress-bar no-print">
          {screens.map((name, i) => (
            <div key={i} className={`progress-step ${i === currentScreen ? 'active' : ''} ${i < currentScreen ? 'completed' : ''}`}>
              {i < currentScreen ? '✓' : i + 1}
              <span>{name}</span>
            </div>
          ))}
        </div>
      )}
      <main className="main-content">{renderCurrentScreen()}</main>
    </div>
  );
}

export default App;
